import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  ModalFooter,
  Textarea,
  Alert,
  AlertIcon,
  AlertDescription,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { TicketService } from '@/lib/services/ticket'
import { LocationService } from '@/lib/services/locationService'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import type { ClientOption, FieldEngineerRecommendation, IncidentLocation } from '@/types/ticket'
import type { FESchedule } from '@/types/parentTicket'
import type { FieldEngineerSummary } from '@/store/parentTicket'
import { FERecommenderPanel } from '@/components/forms/parentTicket/FERecommenderPanel'
import { DispatchSitePinMap } from '@/components/forms/parentTicket/DispatchSitePinMap'
import {
  formatPinnedDisplayName,
  hasFiniteCoords,
  isPinMovedFromOrigin,
  usesCoordinateRecommendations as sourceUsesCoordinateRecommendations,
} from '@/components/forms/parentTicket/dispatchSitePin'
import { MockupFieldLabel } from '@/components/forms/parentTicket/FormPanel'
import { LocationSearchInput } from '@/components/forms/shared/LocationSearchInput'
import {
  resolveAllAssignmentsAfterContextChange,
  buildReassignmentReasonFromEligibility,
  buildInvalidAssignmentKey,
  type AssignmentEligibility,
  type InvalidAssignmentKey,
} from '@/components/forms/parentTicket/feDispatchRevalidate'
import {
  confirmEngineerReassignment,
  resolveLinkedChildTicketId,
} from '@/components/forms/parentTicket/feDispatchReassignment'
import { getEngineerAssignmentConflict } from '@/components/forms/parentTicket/feDispatchPersistence'
import { patchSelectionWithLocation, patchSelectionWithStore, buildLiveFEDispatchSelection } from '@/components/forms/parentTicket/feDispatchSelectionUtils'
import {
  canSeedDispatchLocation,
  normalizeSeedAddress,
  resolveDispatchSeedFromAddress,
  resolveSeedLocationAddress,
  shouldInvalidateAutoSeededLocation,
  storeHasCoordinates,
} from '@/components/forms/parentTicket/seedDispatchLocation'
import {
  ScheduleMapPreview,
  addOneHour,
  activityDateTimesToSelection,
  buildActivityWindowFromIso,
  buildDefaultDispatchWindow,
  isActivityWindowValid,
  selectionToActivityDateTimes,
  timeToMinutes,
  parentLevelFeSchedules,
} from '@/components/forms/parentTicket/feDispatchUtils'
import type {
  CombinedLocationResult,
  FreeTextLocationResult,
  StoreLocationResult,
} from '@/types/location'

export interface FEDispatchSelection {
  /** Original operational schedule; preserved across unsaved replacements. */
  sourceScheduleId?: number | null
  locationType?: 'store' | 'location'
  storeId: number | null
  storeCode: string
  storeName: string
  storeAddress: string
  latitude: number | null
  longitude: number | null
  locationDisplayName?: string
  /** How site coords were obtained (store, nominatim, manual_pin, …). */
  locationSource?: string | null
  incidentLocationMunicipality?: string | null
  incidentLocationProvince?: string | null
  incidentLocationCountry?: string | null
  feUserId: number
  feName: string
  scheduleDate: string
  scheduleTime: string
  scheduleEndTime: string
  contactNumberOverride?: string
  spansMultipleDays: boolean
  scheduleEndDate?: string
  role: 'primary' | 'additional'
  /** 1-based index in the additionalFes array when role is additional. */
  additionalSlot?: number
  /** Linked FE Visit child ticket id when known (from schedule meta). */
  childTicketId?: number | null
  child_ticket_id?: number | null
}

type StoreDetails = {
  locationType?: 'store' | 'location' | null
  storeName?: string | null
  branchCode?: string | null
  storeAddress?: string | null
  contactNumber?: string | null
  latitude?: number | null
  longitude?: number | null
}

type DispatchReferenceLocation = {
  type: 'store' | 'location'
  coordinateSource?: 'store' | 'store_geocoded_address' | 'location' | 'manual_pin' | 'nominatim'
  storeId: number | null
  code?: string | null
  name: string
  address?: string | null
  latitude?: number | null
  longitude?: number | null
  contactNumber?: string | null
  store?: ClientOption | null
  location?: FreeTextLocationResult | null
}

/** True when location, schedule, and (if cert required) brand are all present for recommend. */
function hasCompleteRecommendParams(
  certRequired: boolean,
  brand: number | null,
  locationOk: boolean,
  scheduleIso: string | null,
  fromLocal: string,
  toLocal: string,
): boolean {
  if (!locationOk) return false
  const hasSchedule = Boolean(scheduleIso) || isActivityWindowValid(fromLocal, toLocal)
  if (!hasSchedule) return false
  if (certRequired && brand == null) return false
  return true
}

function hasCoordinatePair(location: Pick<DispatchReferenceLocation, 'latitude' | 'longitude'> | null): boolean {
  return hasFiniteCoords(location?.latitude, location?.longitude)
}

function storeResultToClientOption(result: StoreLocationResult): ClientOption {
  return result.store
}

function locationResultLabel(location: FreeTextLocationResult): string {
  return location.display_name
}

function storeAddressForGeocode(store: ClientOption): string {
  return (
    store.branchAddress ??
    store.locationAddress ??
    store.normalizedAddress ??
    store.location?.address ??
    ''
  ).trim()
}

function storeToReference(
  store: ClientOption,
  geocodedAddress?: FreeTextLocationResult | null,
): DispatchReferenceLocation {
  const address = storeAddressForGeocode(store)
  const useGeocoded = geocodedAddress != null

  return {
    type: 'store',
    coordinateSource: useGeocoded ? 'store_geocoded_address' : 'store',
    storeId: store.storeId ?? null,
    code: store.storeCode ?? store.branchCode,
    name: store.storeName,
    address: useGeocoded ? geocodedAddress.display_name : address,
    contactNumber: store.contactNumber ?? store.storeContact,
    latitude: useGeocoded ? geocodedAddress.latitude : store.latitude ?? store.location?.latitude,
    longitude: useGeocoded ? geocodedAddress.longitude : store.longitude ?? store.location?.longitude,
    store,
    location: geocodedAddress ?? null,
  }
}

function usesCoordinateRecommendations(location: DispatchReferenceLocation): boolean {
  return sourceUsesCoordinateRecommendations(location.coordinateSource) || location.type === 'location'
}

function patchSelectionWithReference(
  selection: FEDispatchSelection,
  location: DispatchReferenceLocation,
): FEDispatchSelection {
  if (location.type === 'location' && location.location) {
    return patchSelectionWithLocation(selection, location.location)
  }

  if (location.type === 'store' && location.store) {
    const patched = patchSelectionWithStore(selection, location.store)
    return {
      ...patched,
      latitude: location.latitude ?? patched.latitude,
      longitude: location.longitude ?? patched.longitude,
      storeAddress: location.address ?? patched.storeAddress,
      locationDisplayName:
        location.coordinateSource === 'store_geocoded_address'
          ? location.address ?? undefined
          : patched.locationDisplayName,
    }
  }

  return selection
}

export function shouldHydrateInitialStore(
  mode: 'immediate' | 'deferred',
  localStore: ClientOption | null,
  localLocation: FreeTextLocationResult | null,
  storeHydrationSkipped: boolean,
): boolean {
  return mode === 'deferred' && !localStore && !localLocation && !storeHydrationSkipped
}

/** Full-card busy overlay state for FE dispatch while backend work is in flight. */
export function resolveFeDispatchCardBusy(input: {
  dispatchCardBusy: boolean
  dispatchCardBusyMessage?: string
  recommendLoading: boolean
  assigningPrimary: boolean
  assigningAdditionalSlotIndex: number | null
  recommendScheduleLoading: boolean
}): { busy: boolean; message: string } {
  const busy =
    input.dispatchCardBusy ||
    input.recommendLoading ||
    input.assigningPrimary ||
    input.assigningAdditionalSlotIndex != null ||
    input.recommendScheduleLoading
  const message = input.dispatchCardBusy
    ? input.dispatchCardBusyMessage || 'Working…'
    : input.recommendLoading
      ? 'Finding field engineers…'
      : input.assigningPrimary || input.assigningAdditionalSlotIndex != null
        ? 'Saving engineer…'
        : input.recommendScheduleLoading
          ? 'Loading availability…'
          : 'Working…'
  return { busy, message }
}

/** Hydrate ParentTicketForm from editTicket only once per ticket id. */
export function shouldHydrateFormFromTicket(
  parentTicketId: string | null | undefined,
  alreadyHydratedTicketId: string | null,
): boolean {
  if (!parentTicketId) return false
  return alreadyHydratedTicketId !== String(parentTicketId)
}

export interface FEDispatchFlowProps {
  clientCode: string
  /** Proposed client for an existing ticket whose request has no client yet. */
  pendingClientId?: number
  initialStoreId?: number | null
  /** Pre-resolved store from GET /tickets/:id (avoids search-by-numeric-id failure). */
  initialStore?: import('@/types/ticket').ClientOption | null
  /** Ticket-level free-text location from GET /tickets/:id when no Store is linked. */
  initialLocation?: IncidentLocation | null
  /**
   * Request site_address used to auto-geocode a dispatch location when the panel opens
   * and no store/location is selected yet. Triggers FE recommendations once located.
   */
  seedLocationAddress?: string | null
  mode: 'immediate' | 'deferred'
  /** Parent form stages every change until its single atomic save. */
  stageChanges?: boolean
  initialPlanningWindow?: { from: string; to: string; durationMinutes?: number; manualOverride?: boolean; overrideReason?: string }
  supportDurationOptions?: Array<{ code: string; label: string }>
  onPlanningWindowChange?: (window: { from: string; to: string; durationMinutes?: number; manualOverride?: boolean; overrideReason?: string }) => void
  onPlanningReasonChange?: (reason: string) => void
  ticketId?: string
  ticketSubject?: string | null
  requestContactNumber?: string | null
  storeId?: number | null
  storeDetails?: StoreDetails | null
  primaryEngineer?: FieldEngineerSummary | null
  additionalEngineers?: FieldEngineerSummary[] | null
  hasExistingAssignment?: boolean
  feScheduleRefreshing?: boolean
  isReadOnly?: boolean
  confirmedSelection?: FEDispatchSelection | null
  confirmedAdditionalSelections?: FEDispatchSelection[]
  onConfirm: (selection: FEDispatchSelection) => void
  /** When set (edit mode), persist dispatch to API on confirm instead of deferring to ticket save. */
  onConfirmPersist?: (selection: FEDispatchSelection) => Promise<{ success: boolean; message?: string }>
  /** When set (edit mode with persisted dispatch), remove FE assignments from API on Clear. */
  onClearPersist?: (
    scope: 'primary' | 'additional' | 'all-additional',
    slotIndex?: number,
    reason?: string,
  ) => Promise<{ success: boolean; message?: string }>
  onClear?: () => void
  onClearAdditional?: (slotIndex?: number) => void
  /** Clears local store selection so user can search again (edit mode). */
  onStoreClear?: () => void
  /** Sync parent form store fields when dispatch store changes. */
  onStoreSelected?: (store: ClientOption) => void
  /** Sync parent form incident location when a free-text location is chosen, seeded, or cleared. */
  onLocationSelected?: (location: FreeTextLocationResult | null) => void
  onSchedulesChange?: () => void | Promise<void>
  onClearEngineer?: (role: 'primary' | 'additional') => void | Promise<void>
  engineerPatchLoading?: 'primary' | 'additional' | null
  /** ISO datetime for conflict checks when no deferred selection schedule exists (e.g. ticket.scheduledAt). */
  preferredScheduleAt?: string | null
  /** Controlled: certification required for FE recommend filtering */
  certificationRequired?: boolean
  /** Controlled: SIMA brand id for certification filtering */
  brandId?: number | null
  onCertificationChange?: (next: { certificationRequired: boolean; brandId: number | null }) => void
  /** Optional display label override when certifications are required */
  certificationLabel?: string | null
  /** Optional override list; normally derived from selected brand name */
  requiredCertifications?: string[]
  locationValidationErrors?: string[]
  /** Normalized Parent Ticket geography used to keep automatic recommendations in-area. */
  locationFilter?: {
    areaClusterId?: number | null
    region?: string | null
    province?: string | null
  }
  /** Parent-level FE schedules for linked task resolution (edit mode). */
  feSchedules?: FESchedule[]
  existingPrimaryScheduleId?: number | null
  existingAdditionalScheduleIds?: number[]
  /** Sync site-pin confirm state to parent (Ready submit gate). */
  onSitePinConfirmedChange?: (confirmed: boolean) => void
  /** Called when unresolved invalid persisted assignments change (edit mode Save guard). */
  onInvalidAssignmentChange?: (hasUnresolved: boolean) => void
  /** Refresh ticket/schedules/tasks after reassignment or withdrawal. */
  onDispatchReconciled?: () => void | Promise<void>
}

export type InvalidAssignmentState = {
  stableKey: InvalidAssignmentKey
  role: 'primary' | 'additional'
  feUserId: number
  feName: string
  eligibility: AssignmentEligibility
  additionalSlot?: number
}

function selectionScheduleToIso(selection: FEDispatchSelection): string {
  return `${selection.scheduleDate}T${selection.scheduleTime}:00`
}

function patchSelectionSchedule(selection: FEDispatchSelection, scheduleIso: string): FEDispatchSelection {
  const d = new Date(scheduleIso)
  const pad = (n: number) => String(n).padStart(2, '0')
  const scheduleDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  const scheduleTime = `${pad(d.getHours())}:${pad(d.getMinutes())}`
  const previousStart = new Date(`${selection.scheduleDate}T${selection.scheduleTime}`)
  const previousEnd = new Date(`${selection.scheduleEndDate || selection.scheduleDate}T${selection.scheduleEndTime}`)
  const duration = previousEnd.getTime() - previousStart.getTime()
  const nextEnd = Number.isFinite(duration) && duration > 0 ? new Date(d.getTime() + duration) : null
  const scheduleEndDate = nextEnd
    ? `${nextEnd.getFullYear()}-${pad(nextEnd.getMonth() + 1)}-${pad(nextEnd.getDate())}`
    : selection.scheduleEndDate
  return {
    ...selection,
    scheduleDate,
    scheduleTime,
    scheduleEndTime: nextEnd ? `${pad(nextEnd.getHours())}:${pad(nextEnd.getMinutes())}` : selection.scheduleEndTime,
    scheduleEndDate,
    spansMultipleDays: scheduleEndDate != null && scheduleEndDate !== scheduleDate,
  }
}

function SectionLabel({ done, children }: { done?: boolean; children: React.ReactNode }) {
  const colors = useThemeColors()
  return (
    <Text
      fontSize="11px"
      fontWeight="700"
      color={done ? 'green.600' : colors.textMuted}
      textTransform="uppercase"
      letterSpacing="wide"
      mb="6px"
      mt={4}
    >
      {children}
    </Text>
  )
}

function getFeUserId(rec: FieldEngineerRecommendation): number | null {
  const id = rec.user_id
  if (id == null || !Number.isFinite(Number(id)) || Number(id) <= 0) return null
  return Number(id)
}

function hasScheduleOverlap(
  schedules: FESchedule[],
  scheduleDate: string,
  scheduleStartMinutes: number,
  durationMinutes = 180,
): boolean {
  const scheduleEndMinutes = scheduleStartMinutes + durationMinutes
  return schedules.some((row) => {
    if (!row.schedule_date?.startsWith(scheduleDate)) return false
    const rowStart = timeToMinutes(row.schedule_time)
    const rowEnd = row.schedule_end_time ? timeToMinutes(row.schedule_end_time) : rowStart + 60
    return scheduleStartMinutes < rowEnd && rowStart < scheduleEndMinutes
  })
}

export default function FEDispatchFlow({
  clientCode,
  pendingClientId,
  initialStoreId,
  initialStore,
  initialLocation,
  seedLocationAddress,
  mode,
  stageChanges = false,
  initialPlanningWindow,
  supportDurationOptions,
  onPlanningWindowChange,
  onPlanningReasonChange,
  ticketId,
  ticketSubject,
  requestContactNumber,
  storeId: externalStoreId,
  storeDetails: externalStoreDetails,
  primaryEngineer,
  additionalEngineers,
  hasExistingAssignment,
  feScheduleRefreshing,
  isReadOnly,
  confirmedSelection,
  confirmedAdditionalSelections,
  onConfirm,
  onConfirmPersist,
  onClearPersist,
  onClear,
  onClearAdditional,
  onStoreClear,
  onStoreSelected,
  onLocationSelected,
  onSchedulesChange,
  onClearEngineer,
  engineerPatchLoading,
  preferredScheduleAt,
  certificationRequired: certificationRequiredProp,
  brandId: brandIdProp,
  onCertificationChange,
  certificationLabel,
  requiredCertifications: requiredCertificationsProp,
  locationValidationErrors,
  locationFilter,
  feSchedules,
  existingPrimaryScheduleId,
  existingAdditionalScheduleIds = [],
  onInvalidAssignmentChange,
  onDispatchReconciled,
  onSitePinConfirmedChange,
}: FEDispatchFlowProps) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const recommendClientCode = clientCode?.trim() || ''

  const [certificationRequired, setCertificationRequired] = useState(Boolean(certificationRequiredProp))
  const [brandId, setBrandId] = useState<number | null>(brandIdProp ?? null)
  const [brands, setBrands] = useState<Array<{ id: number; name: string }>>([])
  const [brandsLoading, setBrandsLoading] = useState(false)

  useEffect(() => {
    setCertificationRequired(Boolean(certificationRequiredProp))
  }, [certificationRequiredProp])

  useEffect(() => {
    setBrandId(brandIdProp ?? null)
  }, [brandIdProp])

  useEffect(() => {
    if (!recommendClientCode) {
      setBrands([])
      return
    }
    let cancelled = false
    setBrandsLoading(true)
    apiClient
      .get('/options/brands', { headers: { 'X-Client-Code': recommendClientCode } })
      .then((response) => {
        if (cancelled) return
        const data = response.data
        const brandsArray = Array.isArray(data) ? data : data?.data || []
        setBrands(
          (Array.isArray(brandsArray) ? brandsArray : [])
            .map((b: { id?: number | string; name?: string }) => ({
              id: Number(b.id),
              name: String(b.name ?? ''),
            }))
            .filter((b: { id: number; name: string }) => Number.isFinite(b.id) && b.id > 0 && b.name.trim() !== ''),
        )
      })
      .catch(() => {
        if (!cancelled) setBrands([])
      })
      .finally(() => {
        if (!cancelled) setBrandsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [recommendClientCode])

  const selectedBrandName = useMemo(() => {
    if (brandId == null) return null
    return brands.find((b) => b.id === brandId)?.name ?? null
  }, [brands, brandId])

  const effectiveRequiredCertifications = useMemo(() => {
    if (requiredCertificationsProp?.length) return requiredCertificationsProp
    if (certificationRequired && selectedBrandName) return [selectedBrandName]
    return []
  }, [requiredCertificationsProp, certificationRequired, selectedBrandName])

  const effectiveCertificationLabel = useMemo(() => {
    if (certificationLabel) return certificationLabel
    if (certificationRequired && selectedBrandName) return selectedBrandName
    return null
  }, [certificationLabel, certificationRequired, selectedBrandName])

  const emitCertificationChange = useCallback(
    (nextRequired: boolean, nextBrandId: number | null) => {
      setCertificationRequired(nextRequired)
      setBrandId(nextRequired ? nextBrandId : null)
      onCertificationChange?.({
        certificationRequired: nextRequired,
        brandId: nextRequired ? nextBrandId : null,
      })
    },
    [onCertificationChange],
  )

  const [localStore, setLocalStore] = useState<ClientOption | null>(null)
  const [localLocation, setLocalLocation] = useState<FreeTextLocationResult | null>(null)
  /** Draft site pin while adjusting; overrides store/search coords until confirm. */
  const [draftSitePin, setDraftSitePin] = useState<{ latitude: number; longitude: number } | null>(null)
  /** Origin coords when the current store/location was selected (detect pin move). */
  const [sitePinOrigin, setSitePinOrigin] = useState<{ latitude: number; longitude: number } | null>(null)
  /** Recommend only after TL confirms the site pin. */
  const [sitePinConfirmed, setSitePinConfirmed] = useState(false)
  const [pinReverseLoading, setPinReverseLoading] = useState(false)
  const [savingStorePin, setSavingStorePin] = useState(false)
  const pinReverseAbortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    onSitePinConfirmedChange?.(sitePinConfirmed)
  }, [sitePinConfirmed, onSitePinConfirmedChange])
  const [useStoreAddressGeocode, setUseStoreAddressGeocode] = useState(false)
  const [storeAddressGeocode, setStoreAddressGeocode] = useState<FreeTextLocationResult | null>(null)
  const [storeAddressGeocoding, setStoreAddressGeocoding] = useState(false)
  const [storeAddressGeocodeError, setStoreAddressGeocodeError] = useState<string | null>(null)
  const [storeEditing, setStoreEditing] = useState(mode === 'deferred')
  const [storeSearchTerm, setStoreSearchTerm] = useState('')
  /** After user clicks Change store, skip re-hydrating from initialStore props. */
  const storeHydrationSkippedRef = useRef(false)
  /** Avoid re-geocoding the same site_address seed repeatedly. */
  const seedLocationAttemptedRef = useRef<string | null>(null)
  /** Address that produced the current automatically seeded localLocation, if any. */
  const autoSeededFromAddressRef = useRef<string | null>(null)
  /**
   * After invalidating an auto-seeded location, skip initial/confirmed hydration so
   * Address A is not restored while Address B is being geocoded.
   * Distinct from storeHydrationSkippedRef (which also blocks canSeed).
   */
  const suppressLocationHydrationRef = useRef(false)
  /** Set when an auto-seed was cleared due to address change; triggers revalidate on next successful seed. */
  const pendingAddressRefreshRevalidateRef = useRef(false)
  /** Latest revalidateAssignments for seed-refresh success without moving the seed effect. */
  const revalidateAssignmentsRef = useRef<
    (options?: {
      scheduleIso?: string | null
      nextLocation?: DispatchReferenceLocation | null
      currentPrimary?: FEDispatchSelection | null
      currentAdditionalFes?: FEDispatchSelection[]
    }) => Promise<void>
  >(async () => {})
  const [seedLocationLoading, setSeedLocationLoading] = useState(false)
  const [seedLocationError, setSeedLocationError] = useState<string | null>(null)

  const effectiveReferenceLocation: DispatchReferenceLocation | null = useMemo(() => {
    const applyDraftPin = (
      base: DispatchReferenceLocation,
      originLat?: number | null,
      originLng?: number | null,
    ): DispatchReferenceLocation => {
      if (!draftSitePin) return base
      const moved = isPinMovedFromOrigin(
        draftSitePin.latitude,
        draftSitePin.longitude,
        originLat ?? sitePinOrigin?.latitude,
        originLng ?? sitePinOrigin?.longitude,
      )
      return {
        ...base,
        latitude: draftSitePin.latitude,
        longitude: draftSitePin.longitude,
        coordinateSource: moved
          ? 'manual_pin'
          : base.coordinateSource ?? (base.type === 'store' ? 'store' : 'location'),
      }
    }

    if (mode === 'deferred' && localStore) {
      const base = storeToReference(
        localStore,
        useStoreAddressGeocode ? storeAddressGeocode : null,
      )
      return applyDraftPin(base, base.latitude, base.longitude)
    }

    if (mode === 'deferred' && localLocation) {
      const base: DispatchReferenceLocation = {
        type: 'location',
        coordinateSource:
          localLocation.source === 'manual_pin' ? 'manual_pin' : 'location',
        storeId: null,
        code: null,
        name: locationResultLabel(localLocation),
        address: localLocation.display_name,
        latitude: localLocation.latitude,
        longitude: localLocation.longitude,
        location: localLocation,
      }
      return applyDraftPin(base, localLocation.latitude, localLocation.longitude)
    }

    if (externalStoreId != null || externalStoreDetails?.storeName) {
      const externalType = externalStoreDetails?.locationType === 'location' ? 'location' : 'store'
      const base: DispatchReferenceLocation = {
        type: externalType,
        coordinateSource: externalType === 'location' ? 'location' : 'store',
        storeId: externalType === 'store' ? externalStoreId ?? null : null,
        code: externalStoreDetails?.branchCode,
        name: externalStoreDetails?.storeName ?? '',
        address: externalStoreDetails?.storeAddress,
        contactNumber: externalStoreDetails?.contactNumber,
        latitude: externalStoreDetails?.latitude,
        longitude: externalStoreDetails?.longitude,
      }
      return applyDraftPin(base, base.latitude, base.longitude)
    }

    if (mode === 'deferred' && draftSitePin) {
      return {
        type: 'location',
        coordinateSource: 'manual_pin',
        storeId: null,
        code: null,
        name: formatPinnedDisplayName(draftSitePin.latitude, draftSitePin.longitude),
        address: formatPinnedDisplayName(draftSitePin.latitude, draftSitePin.longitude),
        latitude: draftSitePin.latitude,
        longitude: draftSitePin.longitude,
        location: {
          type: 'location',
          display_name: formatPinnedDisplayName(draftSitePin.latitude, draftSitePin.longitude),
          latitude: draftSitePin.latitude,
          longitude: draftSitePin.longitude,
          source: 'manual_pin',
        },
      }
    }

    return null
  }, [
    mode,
    localStore,
    localLocation,
    useStoreAddressGeocode,
    storeAddressGeocode,
    externalStoreId,
    externalStoreDetails,
    draftSitePin,
    sitePinOrigin,
  ])

  const effectiveStoreId = effectiveReferenceLocation?.type === 'store'
    ? effectiveReferenceLocation.storeId
    : null
  const effectiveStoreDetails: StoreDetails | null = useMemo(() => {
    if (effectiveReferenceLocation) {
      return {
        storeName: effectiveReferenceLocation.name,
        branchCode: effectiveReferenceLocation.code,
        storeAddress: effectiveReferenceLocation.address,
        contactNumber: effectiveReferenceLocation.contactNumber,
        latitude: effectiveReferenceLocation.latitude,
        longitude: effectiveReferenceLocation.longitude,
      }
    }
    return null
  }, [effectiveReferenceLocation])

  const hasConfirmedSitePin =
    (mode !== 'deferred' || sitePinConfirmed) && hasCoordinatePair(effectiveReferenceLocation)

  const canRecommendFE =
    recommendClientCode.length > 0 &&
    hasConfirmedSitePin &&
    (effectiveStoreId != null ||
      (effectiveReferenceLocation?.type === 'location' && hasCoordinatePair(effectiveReferenceLocation)))
  const recommendFEDisabledReason =
    !effectiveReferenceLocation
      ? mode === 'deferred'
        ? 'Search a store/address or place a pin on the map, then confirm location.'
        : 'Select a store in the task panel above before recommending field engineers.'
      : !hasCoordinatePair(effectiveReferenceLocation)
        ? 'Place a site pin on the map before recommending field engineers.'
      : !sitePinConfirmed
        ? 'Confirm the site pin before recommending field engineers.'
      : recommendClientCode.length === 0
        ? 'Client code is missing — FE recommendation needs the ticket request client.'
        : ''

  const { isOpen: isRecommendOpen, onOpen: onRecommendOpen, onClose: onRecommendClose } = useDisclosure()

  const [recommendations, setRecommendations] = useState<FieldEngineerRecommendation[]>([])
  const [recommendLoading, setRecommendLoading] = useState(false)
  const [assigningPrimary, setAssigningPrimary] = useState(false)
  const [assigningAdditionalSlotIndex, setAssigningAdditionalSlotIndex] = useState<number | null>(null)
  const [assignFailedSignal, setAssignFailedSignal] = useState(0)
  const [dispatchCardBusy, setDispatchCardBusy] = useState(false)
  const [dispatchCardBusyMessage, setDispatchCardBusyMessage] = useState('Working…')
  const recommendRequestRef = useRef(0)
  const lastRecommendStartRef = useRef<{ signature: string; startedAt: number } | null>(null)
  /** Separate counter so revalidateAssignments is not cancelled by fetchRecommendations. */
  const revalidateRequestRef = useRef(0)
  const primaryAssignInFlightRef = useRef(false)
  const backupAssignInFlightRef = useRef<Set<number>>(new Set())
  const [recommendSelectedDate, setRecommendSelectedDate] = useState<Date>(() => new Date())
  const [feAvailabilityByUser, setFeAvailabilityByUser] = useState<Record<number, FESchedule[]>>({})
  const [recommendScheduleLoading, setRecommendScheduleLoading] = useState(false)

  const [localConfirmed, setLocalConfirmed] = useState<FEDispatchSelection | null>(null)
  const [localConfirmedBackups, setLocalConfirmedBackups] = useState<FEDispatchSelection[]>([])
  const [invalidAssignments, setInvalidAssignments] = useState<InvalidAssignmentState[]>([])
  const [reassignModal, setReassignModal] = useState<{
    stableKey: InvalidAssignmentKey
    role: 'primary' | 'additional'
    additionalSlot?: number
    sourceFeUserId: number
    sourceFeName: string
    replacementFeUserId: number
    replacementFeName: string
    reason: string
    proposedSelection?: FEDispatchSelection | null
  } | null>(null)
  const [reassignSubmitting, setReassignSubmitting] = useState(false)
  const [withdrawModal, setWithdrawModal] = useState<{
    stableKey: InvalidAssignmentKey
    role: 'primary' | 'additional'
    additionalSlot?: number
    feUserId: number
    feName: string
    childTicketId: number | null
    affectedTasks: { taskId: number; feName: string; role: string }[]
    reason: string
  } | null>(null)
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false)
  const [primaryDismissed, setPrimaryDismissed] = useState(false)
  const [wantsAdditional, setWantsAdditional] = useState(
    Boolean((confirmedAdditionalSelections && confirmedAdditionalSelections.length > 0) || (additionalEngineers && additionalEngineers.length > 0)),
  )
  /** User explicitly unchecked "Assign additional FE?" — don't auto re-check from ticket meta. */
  const backupOptOutRef = useRef(false)

  const recommendDateStr = useMemo(
    () =>
      `${recommendSelectedDate.getFullYear()}-${String(recommendSelectedDate.getMonth() + 1).padStart(2, '0')}-${String(recommendSelectedDate.getDate()).padStart(2, '0')}`,
    [recommendSelectedDate],
  )

  const timelineHours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), [])
  const today = new Date()
  const isSelectedToday =
    recommendSelectedDate.getFullYear() === today.getFullYear() &&
    recommendSelectedDate.getMonth() === today.getMonth() &&
    recommendSelectedDate.getDate() === today.getDate()

  useEffect(() => {
    storeHydrationSkippedRef.current = false
  }, [initialStoreId, initialStore?.storeId])

  useEffect(() => {
    if (localStore || localLocation) setStoreEditing(false)
  }, [localStore?.storeId, localLocation?.display_name])

  useEffect(() => {
    if (!shouldHydrateInitialStore(mode, localStore, localLocation, storeHydrationSkippedRef.current)) return

    if (initialStore?.storeId) {
      autoSeededFromAddressRef.current = null
      setLocalLocation(null)
      setLocalStore(initialStore)
      setUseStoreAddressGeocode(false)
      setStoreAddressGeocode(null)
      setStoreAddressGeocodeError(null)
      setStoreSearchTerm(`[${initialStore.storeCode}] ${initialStore.storeName}`)
      const lat = initialStore.latitude ?? initialStore.location?.latitude ?? null
      const lng = initialStore.longitude ?? initialStore.location?.longitude ?? null
      if (hasFiniteCoords(lat, lng)) {
        setDraftSitePin({ latitude: Number(lat), longitude: Number(lng) })
        setSitePinOrigin({ latitude: Number(lat), longitude: Number(lng) })
        setSitePinConfirmed(true)
      } else {
        setDraftSitePin(null)
        setSitePinOrigin(null)
        setSitePinConfirmed(false)
      }
      return
    }

    if (!initialStoreId) return

    const searchTerm =
      initialStore?.storeCode && initialStore.storeCode.length >= 3
        ? initialStore.storeCode
        : String(initialStoreId)

    if (searchTerm.length < 3) return

    let cancelled = false
    TicketService.searchStoreOptions(searchTerm, recommendClientCode)
      .then((res) => {
        if (cancelled || !res.success || !res.data?.length) return
        const match =
          res.data.find((s) => s.storeId === initialStoreId) ??
          (initialStore?.storeCode
            ? res.data.find((s) => s.storeCode === initialStore.storeCode)
            : undefined) ??
          res.data[0]
        if (match) {
          autoSeededFromAddressRef.current = null
          setLocalLocation(null)
          setLocalStore(match)
          setUseStoreAddressGeocode(false)
          setStoreAddressGeocode(null)
          setStoreAddressGeocodeError(null)
          setStoreSearchTerm(`[${match.storeCode}] ${match.storeName}`)
          const lat = match.latitude ?? match.location?.latitude ?? null
          const lng = match.longitude ?? match.location?.longitude ?? null
          if (hasFiniteCoords(lat, lng)) {
            setDraftSitePin({ latitude: Number(lat), longitude: Number(lng) })
            setSitePinOrigin({ latitude: Number(lat), longitude: Number(lng) })
            setSitePinConfirmed(true)
          } else {
            setDraftSitePin(null)
            setSitePinOrigin(null)
            setSitePinConfirmed(false)
          }
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [mode, initialStoreId, initialStore, recommendClientCode, localStore, localLocation])

  useEffect(() => {
    if (mode !== 'deferred' || localStore || localLocation) return
    if (storeHydrationSkippedRef.current) return
    if (suppressLocationHydrationRef.current) return
    if (initialLocation?.type === 'location' && initialLocation.latitude != null && initialLocation.longitude != null) {
      const displayName = initialLocation.name || initialLocation.address || 'Selected location'
      const lat = Number(initialLocation.latitude)
      const lng = Number(initialLocation.longitude)
      autoSeededFromAddressRef.current = null
      setLocalLocation({
        type: 'location',
        display_name: displayName,
        latitude: lat,
        longitude: lng,
        place_id: initialLocation.placeId ?? undefined,
        municipality: initialLocation.municipality ?? null,
        province: initialLocation.province ?? null,
        country: initialLocation.country ?? null,
        source: initialLocation.source === 'manual_pin' ? 'manual_pin' : 'nominatim',
      })
      setStoreSearchTerm(displayName)
      setDraftSitePin({ latitude: lat, longitude: lng })
      setSitePinOrigin({ latitude: lat, longitude: lng })
      setSitePinConfirmed(true)
      return
    }
    if (confirmedSelection?.locationType !== 'location') return
    if (
      confirmedSelection.latitude == null ||
      !Number.isFinite(Number(confirmedSelection.latitude)) ||
      confirmedSelection.longitude == null ||
      !Number.isFinite(Number(confirmedSelection.longitude))
    ) {
      return
    }

    const displayName =
      confirmedSelection.locationDisplayName ||
      confirmedSelection.storeAddress ||
      confirmedSelection.storeName ||
      'Selected location'
    autoSeededFromAddressRef.current = null
    setLocalLocation({
      type: 'location',
      display_name: displayName,
      latitude: Number(confirmedSelection.latitude),
      longitude: Number(confirmedSelection.longitude),
      municipality: confirmedSelection.incidentLocationMunicipality ?? null,
      province: confirmedSelection.incidentLocationProvince ?? null,
      country: confirmedSelection.incidentLocationCountry ?? null,
      source: confirmedSelection.locationSource ?? 'nominatim',
    })
    setStoreSearchTerm(displayName)
    setDraftSitePin({
      latitude: Number(confirmedSelection.latitude),
      longitude: Number(confirmedSelection.longitude),
    })
    setSitePinOrigin({
      latitude: Number(confirmedSelection.latitude),
      longitude: Number(confirmedSelection.longitude),
    })
    setSitePinConfirmed(true)
  }, [
    mode,
    localStore,
    localLocation,
    initialLocation,
    confirmedSelection?.locationType,
    confirmedSelection?.latitude,
    confirmedSelection?.longitude,
    confirmedSelection?.locationDisplayName,
    confirmedSelection?.storeAddress,
    confirmedSelection?.storeName,
  ])

  // Auto-locate from ticket/request address when dispatch opens without usable coordinates.
  useEffect(() => {
    const address = normalizeSeedAddress(seedLocationAddress)
    const wasAddressRefresh = shouldInvalidateAutoSeededLocation({
      autoSeededFromAddress: autoSeededFromAddressRef.current,
      nextSeedAddress: address,
    })

    // Clear owned auto-seed on a genuine address change, then return so the next
    // render can geocode without aborting an in-flight request via localLocation deps.
    if (wasAddressRefresh) {
      autoSeededFromAddressRef.current = null
      seedLocationAttemptedRef.current = null
      suppressLocationHydrationRef.current = true
      pendingAddressRefreshRevalidateRef.current = true
      setLocalLocation(null)
      setSeedLocationError(null)
      setStoreSearchTerm(address)
      setDraftSitePin(null)
      setSitePinOrigin(null)
      setSitePinConfirmed(false)
      onLocationSelected?.(null)
      setSeedLocationLoading(false)
      return
    }

    const canSeed = canSeedDispatchLocation({
      mode,
      seedAddress: address,
      hasLocalStore: Boolean(localStore),
      hasLocalLocation: Boolean(localLocation),
      localStoreHasCoordinates: storeHasCoordinates(localStore),
      hasInitialStore: Boolean(initialStoreId || initialStore),
      initialStoreHasCoordinates: storeHasCoordinates(initialStore),
      // While refreshing an auto-seed, ignore stale parent initialLocation until cleared/replaced.
      hasInitialLocation: pendingAddressRefreshRevalidateRef.current
        ? false
        : Boolean(
            initialLocation?.type === 'location' &&
              initialLocation.latitude != null &&
              initialLocation.longitude != null,
          ),
      hydrationSkipped: storeHydrationSkippedRef.current,
    })

    if (!canSeed) {
      // Avoid leaving a spinner up after a cancelled in-flight seed (dep change / remount).
      setSeedLocationLoading(false)
      return
    }

    // Only skip after a completed attempt for this address (not after an aborted one).
    if (seedLocationAttemptedRef.current === address) {
      setSeedLocationLoading(false)
      return
    }

    let cancelled = false
    const controller = new AbortController()
    setSeedLocationLoading(true)
    setSeedLocationError(null)
    setStoreSearchTerm(address)

    void (async () => {
      try {
        const location = await resolveDispatchSeedFromAddress(address, controller.signal)
        if (cancelled) return

        seedLocationAttemptedRef.current = address
        if (!location) {
          pendingAddressRefreshRevalidateRef.current = false
          setSeedLocationError('Could not locate this request address. Search manually below.')
          return
        }

        autoSeededFromAddressRef.current = address
        suppressLocationHydrationRef.current = false
        setLocalStore(null)
        setLocalLocation(location)
        setUseStoreAddressGeocode(false)
        setStoreAddressGeocode(null)
        setStoreAddressGeocodeError(null)
        setStoreSearchTerm(location.display_name)
        setStoreEditing(false)
        setDraftSitePin({ latitude: location.latitude, longitude: location.longitude })
        setSitePinOrigin({ latitude: location.latitude, longitude: location.longitude })
        setSitePinConfirmed(false)
        onLocationSelected?.(location)

        if (pendingAddressRefreshRevalidateRef.current) {
          pendingAddressRefreshRevalidateRef.current = false
          const nextLocation: DispatchReferenceLocation = {
            type: 'location',
            storeId: null,
            code: null,
            name: locationResultLabel(location),
            address: location.display_name,
            latitude: location.latitude,
            longitude: location.longitude,
            location,
          }
          void revalidateAssignmentsRef.current({ nextLocation })
        }
      } catch {
        if (cancelled) return
        seedLocationAttemptedRef.current = address
        pendingAddressRefreshRevalidateRef.current = false
        setSeedLocationError('Could not locate this request address. Search manually below.')
      } finally {
        setSeedLocationLoading(false)
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [
    mode,
    seedLocationAddress,
    localStore,
    localLocation,
    initialStoreId,
    initialStore,
    initialLocation?.type,
    initialLocation?.latitude,
    initialLocation?.longitude,
    onLocationSelected,
  ])

  const fetchFEAvailabilityForUsers = useCallback(
    async (dateStr: string, feUserIds: number[], replaceAll = true) => {
      const uniqueIds = [...new Set(feUserIds.filter((id) => Number.isFinite(id) && id > 0))]
      if (uniqueIds.length === 0) {
        if (replaceAll) setFeAvailabilityByUser({})
        return
      }

      if (replaceAll) setRecommendScheduleLoading(true)
      try {
        const res =
          effectiveStoreId != null
            ? await TicketService.getFEAvailabilityGlobal(
                Number(effectiveStoreId),
                uniqueIds,
                dateStr,
                recommendClientCode,
                ticketId ?? undefined,
              )
            : await TicketService.getFEAvailabilityGlobalForLocation(
                uniqueIds,
                dateStr,
                recommendClientCode,
                ticketId ?? undefined,
              )

        const next: Record<number, FESchedule[]> = {}
        for (const id of uniqueIds) {
          next[id] = res.success && res.data ? (res.data[id] ?? []) : []
        }

        if (replaceAll) {
          setFeAvailabilityByUser(next)
        } else {
          setFeAvailabilityByUser((prev) => ({ ...prev, ...next }))
        }
      } finally {
        if (replaceAll) setRecommendScheduleLoading(false)
      }
    },
    [ticketId, effectiveStoreId, recommendClientCode],
  )

  const fetchFEAvailabilityForDate = useCallback(
    async (dateStr: string) => {
      if (recommendations.length === 0) return
      const feUserIds = recommendations
        .map(getFeUserId)
        .filter((id): id is number => id != null)
      await fetchFEAvailabilityForUsers(dateStr, feUserIds, true)
    },
    [recommendations, fetchFEAvailabilityForUsers],
  )

  useEffect(() => {
    if (isRecommendOpen && recommendations.length > 0) {
      void fetchFEAvailabilityForDate(recommendDateStr)
    }
  }, [isRecommendOpen, recommendations, recommendDateStr, fetchFEAvailabilityForDate])

  useEffect(() => {
    if (mode === 'deferred' && recommendations.length > 0) {
      void fetchFEAvailabilityForDate(recommendDateStr)
    }
  }, [mode, recommendations, recommendDateStr, fetchFEAvailabilityForDate])

  const getTimelineBlocks = useCallback(
    (feUserId: number): { startHour: number; endHour: number; schedule: FESchedule | null }[] => {
      const list = (feAvailabilityByUser[feUserId] ?? []).filter((s) => s.schedule_date?.startsWith(recommendDateStr))
      const hourToSchedule: (FESchedule | null)[] = Array(24).fill(null)
      for (const s of list) {
        const startM = timeToMinutes(s.schedule_time)
        const endM = s.schedule_end_time ? timeToMinutes(s.schedule_end_time) : startM + 60
        const startHour = Math.max(0, Math.floor(startM / 60))
        const endHour = Math.min(24, Math.floor(endM / 60) + 1)
        for (let h = startHour; h < endHour; h++) hourToSchedule[h] = s
      }
      const blocks: { startHour: number; endHour: number; schedule: FESchedule | null }[] = []
      let i = 0
      while (i < 24) {
        const s = hourToSchedule[i]
        let end = i + 1
        while (end < 24 && hourToSchedule[end] === s) end++
        blocks.push({ startHour: i, endHour: end, schedule: s })
        i = end
      }
      return blocks
    },
    [feAvailabilityByUser, recommendDateStr],
  )

  const fetchRecommendations = useCallback(async (options?: { limit?: number }): Promise<FieldEngineerRecommendation[]> => {
    if (!canRecommendFE || !effectiveReferenceLocation) return []

    const limit = options?.limit
    const signature = JSON.stringify({
      referenceType: effectiveReferenceLocation.type,
      storeId: effectiveStoreId,
      latitude: effectiveReferenceLocation.latitude,
      longitude: effectiveReferenceLocation.longitude,
      limit: limit ?? 10,
      certifications: effectiveRequiredCertifications,
      locationFilter,
    })
    const now = Date.now()
    if (
      lastRecommendStartRef.current?.signature === signature &&
      now - lastRecommendStartRef.current.startedAt < 750
    ) {
      return []
    }
    lastRecommendStartRef.current = { signature, startedAt: now }

    const requestId = ++recommendRequestRef.current
    setRecommendLoading(true)

    try {
      const useStoreRecommendations = mode === 'deferred' || !ticketId
      const recommendOptions = {
        ...(limit != null ? { limit } : {}),
        ...(effectiveRequiredCertifications.length
          ? { requiredCertifications: effectiveRequiredCertifications }
          : {}),
        ...locationFilter,
      }
      const res = useStoreRecommendations
        ? effectiveReferenceLocation.type === 'store' &&
          effectiveStoreId != null &&
          !usesCoordinateRecommendations(effectiveReferenceLocation)
          ? await TicketService.recommendFieldEngineers(Number(effectiveStoreId), recommendClientCode, recommendOptions)
          : await TicketService.recommendFieldEngineersByLocation(
              Number(effectiveReferenceLocation.latitude),
              Number(effectiveReferenceLocation.longitude),
              recommendClientCode,
              recommendOptions,
            )
        : await TicketService.recommendEngineersForTicket(ticketId, recommendOptions)

      if (requestId !== recommendRequestRef.current) return []

      if (res.success && res.data) {
        setRecommendations(res.data)
        return res.data
      }

      if (!res.success && res.message) {
        showError(res.message)
      }
      setRecommendations([])
      return []
    } finally {
      if (requestId === recommendRequestRef.current) {
        setRecommendLoading(false)
      }
    }
  }, [canRecommendFE, effectiveReferenceLocation, effectiveStoreId, mode, ticketId, recommendClientCode, showError, effectiveRequiredCertifications, locationFilter])

  useEffect(() => {
    if (!canRecommendFE || !effectiveReferenceLocation) {
      setRecommendations([])
      setRecommendLoading(false)
      return
    }

    if (mode !== 'deferred' && !ticketId) {
      setRecommendations([])
      setRecommendLoading(false)
      return
    }

    const timer = window.setTimeout(() => {
      void fetchRecommendations()
    }, 200)
    return () => window.clearTimeout(timer)
  }, [mode, canRecommendFE, ticketId, effectiveStoreId, effectiveReferenceLocation, recommendClientCode, fetchRecommendations])

  const displaySelection = confirmedSelection ?? localConfirmed
  const displayBackupSelections =
    confirmedAdditionalSelections !== undefined ? confirmedAdditionalSelections : localConfirmedBackups
  const hasFeAssignment = Boolean(displaySelection || displayBackupSelections.length > 0)

  const parentLevelSchedules = useMemo(
    () => parentLevelFeSchedules(feSchedules ?? []),
    [feSchedules],
  )

  const syncInvalidAssignmentState = useCallback(
    (result: ReturnType<typeof resolveAllAssignmentsAfterContextChange>, currentPrimary: FEDispatchSelection | null, currentAdditionalFes: FEDispatchSelection[]) => {
      const nextInvalid: InvalidAssignmentState[] = []
      if (currentPrimary && result.primaryEligibility && (!result.primaryEligibility.eligible || result.primaryEligibility.unknown)) {
        nextInvalid.push({
          stableKey: buildInvalidAssignmentKey('primary', currentPrimary.feUserId),
          role: 'primary',
          feUserId: currentPrimary.feUserId,
          feName: currentPrimary.feName,
          eligibility: result.primaryEligibility,
        })
      }
      for (let i = 0; i < currentAdditionalFes.length; i++) {
        const fe = currentAdditionalFes[i]
        const elig = result.additionalEligibilities[i]
        if (fe && elig && (!elig.eligible || elig.unknown)) {
          nextInvalid.push({
            stableKey: buildInvalidAssignmentKey('additional', fe.feUserId, fe.additionalSlot ?? i + 1),
            role: 'additional',
            feUserId: fe.feUserId,
            feName: fe.feName,
            eligibility: elig,
            additionalSlot: fe.additionalSlot ?? i + 1,
          })
        }
      }
      setInvalidAssignments(nextInvalid)
      onInvalidAssignmentChange?.(nextInvalid.length > 0)
    },
    [onInvalidAssignmentChange],
  )

  const activityWindow = useMemo(() => {
    if (displaySelection) return selectionToActivityDateTimes(displaySelection)
    return buildActivityWindowFromIso(preferredScheduleAt)
  }, [displaySelection, preferredScheduleAt])

  const [activityFromLocal, setActivityFromLocal] = useState(initialPlanningWindow?.from ?? activityWindow.fromLocal)
  const [activityToLocal, setActivityToLocal] = useState(initialPlanningWindow?.to ?? activityWindow.toLocal)
  /** When true, skip syncing From/To from displaySelection (user is editing staged dates). */
  const activityTouchedRef = useRef(false)
  const lastPrimaryFeUserIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (stageChanges && initialPlanningWindow) {
      activityTouchedRef.current = true
      setActivityFromLocal(initialPlanningWindow.from)
      setActivityToLocal(initialPlanningWindow.to)
    }
  }, [stageChanges, initialPlanningWindow])

  useEffect(() => {
    if (activityTouchedRef.current) return
    setActivityFromLocal(activityWindow.fromLocal)
    setActivityToLocal(activityWindow.toLocal)
  }, [activityWindow.fromLocal, activityWindow.toLocal])

  // Re-hydrate activity inputs only when the primary engineer identity changes (not on persist round-trips).
  useEffect(() => {
    const nextId = displaySelection?.feUserId ?? null
    const prevId = lastPrimaryFeUserIdRef.current
    if (prevId != null && nextId != null && prevId !== nextId) {
      activityTouchedRef.current = false
      setActivityFromLocal(activityWindow.fromLocal)
      setActivityToLocal(activityWindow.toLocal)
    }
    lastPrimaryFeUserIdRef.current = nextId
  }, [displaySelection?.feUserId, activityWindow.fromLocal, activityWindow.toLocal])

  const activeScheduleIso = useMemo(() => {
    if (displaySelection) return selectionScheduleToIso(displaySelection)
    if (preferredScheduleAt) return preferredScheduleAt
    return null
  }, [displaySelection, preferredScheduleAt])

  const resolveScheduleIdForRole = useCallback(
    (role: 'primary' | 'additional', slotIndex = 0): number | null => {
      if (role === 'primary') {
        return existingPrimaryScheduleId ?? null
      }
      return existingAdditionalScheduleIds[slotIndex] ?? null
    },
    [existingPrimaryScheduleId, existingAdditionalScheduleIds],
  )

  const revalidateAssignments = useCallback(
    async (
      options?: {
        scheduleIso?: string | null
        nextLocation?: DispatchReferenceLocation | null
        currentPrimary?: FEDispatchSelection | null
        currentAdditionalFes?: FEDispatchSelection[]
      },
    ) => {
      const currentPrimary = options && 'currentPrimary' in options
        ? options.currentPrimary ?? null
        : displaySelection
      const currentAdditionalFes = options?.currentAdditionalFes ?? displayBackupSelections

      const nextLocation = options?.nextLocation ?? effectiveReferenceLocation
      const targetStoreId = nextLocation?.type === 'store' ? Number(nextLocation.storeId ?? effectiveStoreId) : null
      const hasTargetStore = targetStoreId != null && Number.isFinite(targetStoreId) && targetStoreId > 0
      const hasTargetCoordinates = hasCoordinatePair(nextLocation)
      if (!hasTargetStore && !hasTargetCoordinates) return

      const scheduleIso = options?.scheduleIso ?? activeScheduleIso ?? null
      const requestId = ++revalidateRequestRef.current
      if (stageChanges) onInvalidAssignmentChange?.(true)
      setRecommendLoading(true)

      try {
        // Always refresh recommendations (Apply schedule / location change), even before an FE is assigned.
        const recResponse = hasTargetStore && nextLocation && !usesCoordinateRecommendations(nextLocation)
          ? await TicketService.recommendFieldEngineers(targetStoreId, recommendClientCode || undefined, {
              schedule: scheduleIso ?? undefined,
              ...locationFilter,
              ...(effectiveRequiredCertifications.length
                ? { requiredCertifications: effectiveRequiredCertifications }
                : {}),
            })
          : await TicketService.recommendFieldEngineersByLocation(
              Number(nextLocation?.latitude),
              Number(nextLocation?.longitude),
              recommendClientCode || undefined,
              {
                schedule: scheduleIso ?? undefined,
                ...locationFilter,
                ...(effectiveRequiredCertifications.length
                  ? { requiredCertifications: effectiveRequiredCertifications }
                  : {}),
              },
            )

        if (requestId !== revalidateRequestRef.current) return

        if (!recResponse.success || !recResponse.data) {
          if (recResponse.message) showError(recResponse.message)
          return
        }

        const latestRecommendations = recResponse.data
        setRecommendations(latestRecommendations)

        // Nothing assigned yet — recommendations above are enough (typical Apply schedule path).
        if (!currentPrimary && currentAdditionalFes.length === 0) return

        let unavailableFeUserIds: Set<number> | undefined
        if (scheduleIso) {
          const assignedIds = [currentPrimary?.feUserId, ...currentAdditionalFes.map((b) => b.feUserId)].filter(
            (id): id is number => Number.isFinite(Number(id)),
          )
          if (assignedIds.length > 0) {
            const d = new Date(scheduleIso)
            const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
              d.getDate(),
            ).padStart(2, '0')}`
            const scheduleStartMinutes = d.getHours() * 60 + d.getMinutes()
            const availability = hasTargetStore
              ? await TicketService.getFEAvailabilityGlobal(
                  targetStoreId,
                  assignedIds,
                  date,
                  recommendClientCode || undefined,
                  ticketId,
                )
              : await TicketService.getFEAvailabilityGlobalForLocation(
                  assignedIds,
                  date,
                  recommendClientCode || undefined,
                  ticketId,
                )
            if (availability.success && availability.data) {
              unavailableFeUserIds = new Set<number>()
              for (const feUserId of assignedIds) {
                const schedules = availability.data[feUserId] ?? []
                if (hasScheduleOverlap(schedules, date, scheduleStartMinutes, 180)) {
                  unavailableFeUserIds.add(feUserId)
                }
              }
            }
          }
        }

        if (requestId !== revalidateRequestRef.current) return

        const scheduleExplicitlyChanged = Boolean(options && 'scheduleIso' in options && options.scheduleIso)
        let patchedCurrentPrimary = currentPrimary
          ? (nextLocation ? patchSelectionWithReference(currentPrimary, nextLocation) : currentPrimary)
          : null
        if (patchedCurrentPrimary && scheduleExplicitlyChanged && options?.scheduleIso && !('currentPrimary' in options)) {
          patchedCurrentPrimary = patchSelectionSchedule(patchedCurrentPrimary, options.scheduleIso)
        }
        const patchedAdditionalFes: FEDispatchSelection[] = currentAdditionalFes.map((backup, index) => {
          let patched = nextLocation ? patchSelectionWithReference(backup, nextLocation) : backup
          if (scheduleExplicitlyChanged && options?.scheduleIso && !('currentAdditionalFes' in options)) {
            patched = patchSelectionSchedule(patched, options.scheduleIso)
          }
          return { ...patched, role: 'additional' as const, additionalSlot: index + 1 }
        })

        const authoritativeByFeUserId: Record<number, {
          fe_user_id: number
          eligible: boolean
          reasons: string[]
          reason_codes?: string[]
        }> = {}
        let eligibilityUnknown = false
        const stagedAssignments = [
          ...(patchedCurrentPrimary ? [{ selection: patchedCurrentPrimary, scheduleId: resolveScheduleIdForRole('primary', 0) }] : []),
          ...patchedAdditionalFes.map((selection, index) => ({
            selection,
            scheduleId: resolveScheduleIdForRole('additional', index),
          })),
        ]

        if (ticketId && stagedAssignments.length > 0) {
          const responses = await Promise.all(
            stagedAssignments.map(({ selection, scheduleId }) =>
              TicketService.checkEngineerEligibility(String(ticketId), [selection.feUserId], {
                clientId: pendingClientId,
                schedule_date: selection.scheduleDate,
                schedule_time: selection.scheduleTime || undefined,
                schedule_end_date: selection.spansMultipleDays
                  ? (selection.scheduleEndDate || undefined)
                  : selection.scheduleDate,
                schedule_end_time: selection.scheduleEndTime || undefined,
                latitude: selection.latitude ?? undefined,
                longitude: selection.longitude ?? undefined,
                location_address: selection.storeAddress || undefined,
                requiredCertifications: effectiveRequiredCertifications,
                exclude_schedule_id: parentLevelSchedules.find((s) => s.id === scheduleId && s.fe_user_id === selection.feUserId)?.id,
                role: selection.role,
              }),
            ),
          )
          if (requestId !== revalidateRequestRef.current) return
          for (const eligibilityResponse of responses) {
            if (eligibilityResponse.success && eligibilityResponse.data) {
              Object.assign(authoritativeByFeUserId, eligibilityResponse.data)
            } else {
              eligibilityUnknown = true
            }
          }
        }

        const result = resolveAllAssignmentsAfterContextChange({
          primary: patchedCurrentPrimary,
          additionalFes: patchedAdditionalFes,
          recommendations: latestRecommendations,
          scheduleIso,
          unavailableFeUserIds,
          authoritativeByFeUserId,
          eligibilityUnknown,
        })

        syncInvalidAssignmentState(result, patchedCurrentPrimary, patchedAdditionalFes)

        // Local selection patch only — no API mutation during revalidate.
        if (patchedCurrentPrimary) {
          onConfirm(patchedCurrentPrimary)
          setLocalConfirmed(patchedCurrentPrimary)
        }
        if (patchedAdditionalFes.length > 0) {
          patchedAdditionalFes.forEach((backup) => onConfirm(backup))
          setLocalConfirmedBackups(patchedAdditionalFes)
        }

        if (nextLocation?.type === 'store' && nextLocation.store) {
          onStoreSelected?.(nextLocation.store)
        }
      } catch {
        if (requestId === revalidateRequestRef.current) {
          onInvalidAssignmentChange?.(true)
          showError('Eligibility could not be verified. Refresh recommendations before applying changes; you can still save a draft.')
        }
      } finally {
        if (requestId === revalidateRequestRef.current) {
          setRecommendLoading(false)
        }
      }
    },
    [
      activeScheduleIso,
      displaySelection,
      displayBackupSelections,
      effectiveStoreId,
      effectiveReferenceLocation,
      ticketId,
      recommendClientCode,
      effectiveRequiredCertifications,
      locationFilter,
      onConfirm,
      pendingClientId,
      onStoreSelected,
      showError,
      syncInvalidAssignmentState,
      resolveScheduleIdForRole,
    ],
  )

  revalidateAssignmentsRef.current = revalidateAssignments

  const prevRequiredCertsRef = useRef<string[] | null>(null)

  useEffect(() => {
    const next = effectiveRequiredCertifications
    const prev = prevRequiredCertsRef.current
    prevRequiredCertsRef.current = next

    // Skip mount; only react to real certification/brand filter changes.
    if (prev === null) return
    if (prev.length === next.length && prev.every((v, i) => v === next[i])) return

    if (
      !hasCompleteRecommendParams(
        certificationRequired,
        brandId,
        Boolean(canRecommendFE && effectiveReferenceLocation),
        activeScheduleIso,
        activityFromLocal,
        activityToLocal,
      )
    ) {
      return
    }

    void revalidateAssignments({ scheduleIso: activeScheduleIso })
  }, [
    effectiveRequiredCertifications,
    certificationRequired,
    brandId,
    canRecommendFE,
    effectiveReferenceLocation,
    activeScheduleIso,
    activityFromLocal,
    activityToLocal,
    revalidateAssignments,
  ])

  useEffect(() => {
    if (!useStoreAddressGeocode || !localStore) {
      setStoreAddressGeocoding(false)
      return
    }

    const address = storeAddressForGeocode(localStore)
    if (address.length < 3) {
      setStoreAddressGeocode(null)
      setStoreAddressGeocodeError('Store address is missing, so geocoding cannot be used.')
      setStoreAddressGeocoding(false)
      return
    }

    const controller = new AbortController()
    setStoreAddressGeocoding(true)
    setStoreAddressGeocodeError(null)

    LocationService.searchLocation(address, controller.signal)
      .then((results) => {
        if (controller.signal.aborted) return
        const first = results[0] ?? null
        setStoreAddressGeocode(first)
        if (!first) {
          setStoreAddressGeocodeError('No geocoded match found for this store address. Using store coordinates.')
          return
        }

        setDraftSitePin({ latitude: first.latitude, longitude: first.longitude })
        setSitePinOrigin({ latitude: first.latitude, longitude: first.longitude })
        setSitePinConfirmed(false)

        const nextLocation = storeToReference(localStore, first)
        const nextScheduleIso = displaySelection
          ? selectionScheduleToIso(displaySelection)
          : preferredScheduleAt ?? null
        void revalidateAssignments({ nextLocation, scheduleIso: nextScheduleIso })
      })
      .catch(() => {
        if (controller.signal.aborted) return
        setStoreAddressGeocode(null)
        setStoreAddressGeocodeError('Unable to geocode this store address. Using store coordinates.')
      })
      .finally(() => {
        if (!controller.signal.aborted) setStoreAddressGeocoding(false)
      })

    return () => {
      controller.abort()
    }
  }, [
    useStoreAddressGeocode,
    localStore,
    displaySelection,
    preferredScheduleAt,
    revalidateAssignments,
  ])

  const primaryEngineerUserId = primaryDismissed
    ? displaySelection?.feUserId ?? null
    : displaySelection?.feUserId ?? primaryEngineer?.user_id ?? null
  const primaryEngineerName = primaryDismissed
    ? displaySelection?.feName ?? null
    : displaySelection?.feName ?? primaryEngineer?.full_name ?? null

  const additionalSlots = useMemo(
    () =>
      displayBackupSelections.map((backup, index) => ({
        id: backup.feUserId,
        name: backup.feName ?? additionalEngineers?.[index]?.full_name ?? null,
      })),
    [displayBackupSelections, additionalEngineers],
  )

  useEffect(() => {
    if (!primaryEngineer?.user_id) setPrimaryDismissed(false)
  }, [primaryEngineer?.user_id])

  useEffect(() => {
    if (backupOptOutRef.current) return
    if (displayBackupSelections.length > 0 || (additionalEngineers && additionalEngineers.length > 0)) {
      setWantsAdditional(true)
    }
  }, [displayBackupSelections.length, additionalEngineers])

  const handleWantsBackupChange = useCallback(
    async (checked: boolean) => {
      if (checked) {
        backupOptOutRef.current = false
        setWantsAdditional(true)
        return
      }

      backupOptOutRef.current = true
      setWantsAdditional(false)
      setLocalConfirmedBackups([])

      const hasLocalOrTicketBackups =
        displayBackupSelections.length > 0 || Boolean(additionalEngineers && additionalEngineers.length > 0)

      if (!stageChanges && onClearPersist && hasExistingAssignment && hasLocalOrTicketBackups) {
        const res = await onClearPersist('all-additional')
        if (!res.success) {
          backupOptOutRef.current = false
          setWantsAdditional(true)
          showError(res.message ?? 'Failed to clear additional FEs')
          return
        }
        onClearAdditional?.()
        showSuccess(
          'Additional FE removed',
          'Additional FE assignments were cleared from this ticket.',
        )
        void fetchRecommendations({ limit: 50 })
        return
      }

      onClearAdditional?.()
      if (mode === 'deferred' && !stageChanges) {
        for (const backup of displayBackupSelections) {
          void TicketService.unlockFieldEngineer(backup.feUserId).catch(() => undefined)
        }
      }
      showSuccess('Additional FE cleared')
    },
    [
      additionalEngineers,
      displayBackupSelections,
      fetchRecommendations,
      hasExistingAssignment,
      mode,
      onClearAdditional,
      onClearPersist,
      showError,
      showSuccess,
    ],
  )

  const handleScheduleConflictChange = useCallback(
    async (scheduleIso: string) => {
      activityTouchedRef.current = false
      const nextPrimary = displaySelection
        ? patchSelectionSchedule(displaySelection, scheduleIso)
        : null
      const nextAdditionalFes = displayBackupSelections.map((backup) =>
        patchSelectionSchedule(backup, scheduleIso),
      )

      if (displaySelection && onConfirm) {
        onConfirm(nextPrimary!)
      }
      nextAdditionalFes.forEach((backup) => {
        onConfirm(backup)
      })
      await revalidateAssignments({
        scheduleIso,
        currentPrimary: nextPrimary,
        currentAdditionalFes: nextAdditionalFes,
      })
    },
    [
      displaySelection,
      displayBackupSelections,
      onConfirm,
      revalidateAssignments,
    ],
  )

  const handleActivityScheduleChange = useCallback(
    (fromLocal: string, toLocal: string, overrideReason?: string) => {
      activityTouchedRef.current = true
      setActivityFromLocal(fromLocal)
      setActivityToLocal(toLocal)
      const durationMinutes = isActivityWindowValid(fromLocal, toLocal)
        ? Math.round((new Date(toLocal).getTime() - new Date(fromLocal).getTime()) / 60000)
        : undefined
      onPlanningWindowChange?.({
        from: fromLocal,
        to: toLocal,
        durationMinutes: overrideReason !== undefined ? undefined : durationMinutes,
        manualOverride: overrideReason !== undefined,
        overrideReason: overrideReason !== undefined ? overrideReason : undefined,
      })
      if (stageChanges && !isActivityWindowValid(fromLocal, toLocal)) {
        ++revalidateRequestRef.current
        setRecommendLoading(false)
        onInvalidAssignmentChange?.(true)
      }
    },
    [],
  )

  const handleActivityScheduleApply = useCallback(
    (fromLocal: string, toLocal: string) => {
      if (!isActivityWindowValid(fromLocal, toLocal)) return
      activityTouchedRef.current = false
      const patch = activityDateTimesToSelection(fromLocal, toLocal)
      const fromIso = new Date(fromLocal).toISOString()
      const nextPrimary = displaySelection
        ? { ...displaySelection, ...patch, role: 'primary' as const }
        : null
      const nextAdditionalFes = displayBackupSelections.map((backup, index) => ({
        ...backup,
        ...patch,
        role: 'additional' as const,
        additionalSlot: index + 1,
      }))

      if (nextPrimary) onConfirm(nextPrimary)
      nextAdditionalFes.forEach((backup) => onConfirm(backup))

      void (async () => {
        await revalidateAssignments({
          scheduleIso: fromIso,
          currentPrimary: nextPrimary,
          currentAdditionalFes: nextAdditionalFes,
        })
      })()
    },
    [
      displaySelection,
      displayBackupSelections,
      onConfirm,
      revalidateAssignments,
    ],
  )

  const handlePromoteAdditional = useCallback(() => {
    const [first, ...rest] = displayBackupSelections
    if (!first) return
    const promoted: FEDispatchSelection = { ...first, role: 'primary', additionalSlot: undefined }
    onConfirm(promoted)
    setLocalConfirmed(promoted)
    setPrimaryDismissed(false)
    onClearAdditional?.()
    const shifted = rest.map((backup, index) => ({
      ...backup,
      role: 'additional' as const,
      additionalSlot: index + 1,
    }))
    shifted.forEach((backup) => onConfirm(backup))
    setLocalConfirmedBackups(shifted)
    showSuccess('Additional FE promoted to primary')
  }, [displayBackupSelections, onConfirm, onClearAdditional, showSuccess])

  const handleFindNewPrimary = useCallback(() => {
    if (mode === 'deferred') {
      onClear?.()
    } else if (onClearEngineer) {
      void onClearEngineer('primary')
    }
    if (ticketId && activeScheduleIso) {
      void TicketService.recommendEngineersForTicket(ticketId, {
        schedule: activeScheduleIso,
        ...(effectiveRequiredCertifications.length
          ? { requiredCertifications: effectiveRequiredCertifications }
          : {}),
      }).then((res) => {
        if (res.success && res.data) setRecommendations(res.data)
      })
    } else if (ticketId) {
      void fetchRecommendations()
    }
  }, [mode, onClear, onClearEngineer, ticketId, activeScheduleIso, fetchRecommendations, effectiveRequiredCertifications])

  const handleClearPrimary = useCallback(async (reason?: string): Promise<boolean> => {
    if (stageChanges) {
      if (reason?.trim()) onPlanningReasonChange?.(reason.trim())
      setInvalidAssignments([])
      onInvalidAssignmentChange?.(false)
    }
    setPrimaryDismissed(true)
    setDispatchCardBusy(true)
    setDispatchCardBusyMessage('Clearing assignment…')
    try {
      if (!stageChanges && onClearPersist && hasExistingAssignment) {
        const res = await onClearPersist('primary', undefined, reason)
        if (!res.success) {
          showError(res.message ?? 'Failed to clear field engineer assignment')
          return false
        }
        onClear?.()
        onClearAdditional?.()
        setLocalConfirmed(null)
        setLocalConfirmedBackups([])
        showSuccess(
          'Field engineer removed',
          'Primary and additional FE assignments were cleared from this ticket.',
        )
        // Recommend refresh is triggered once by FERecommenderPanel onManualModeOpen
        // after clear completes and change-primary opens — avoid a second loading flicker.
        return true
      }
      if (mode === 'deferred') {
        onClear?.()
        onClearAdditional?.()
        setLocalConfirmed(null)
        setLocalConfirmedBackups([])
        showSuccess('Field engineer cleared', 'Primary and additional FE selections were removed.')
        return true
      }
      if (onClearEngineer) {
        setLocalConfirmed(null)
        void onClearEngineer('primary')
        showSuccess('Field engineer cleared')
        return true
      }
      return true
    } finally {
      setDispatchCardBusy(false)
    }
  }, [mode, onClear, onClearAdditional, onClearEngineer, onClearPersist, hasExistingAssignment, showError, showSuccess])

  const handleClearBackup = useCallback(async (slotIndex?: number, reason?: string): Promise<boolean> => {
    if (stageChanges && reason?.trim()) onPlanningReasonChange?.(reason.trim())
    const reindex = (list: FEDispatchSelection[]) =>
      list.map((backup, index) => ({
        ...backup,
        role: 'additional' as const,
        additionalSlot: index + 1,
      }))

    const applyLocal = () => {
      if (slotIndex == null) {
        setLocalConfirmedBackups([])
        return
      }
      setLocalConfirmedBackups((prev) => reindex(prev.filter((_, i) => i !== slotIndex)))
    }

    setDispatchCardBusy(true)
    setDispatchCardBusyMessage('Clearing assignment…')
    try {
      if (!stageChanges && onClearPersist && hasExistingAssignment) {
        const res = await onClearPersist('additional', slotIndex, reason)
        if (!res.success) {
          showError(res.message ?? 'Failed to clear additional FE')
          return false
        }
        onClearAdditional?.(slotIndex)
        applyLocal()
        showSuccess(
          'Additional FE removed',
          'The additional FE assignment was cleared from this ticket.',
        )
        // Refresh happens via onManualModeOpen when pick mode opens, or onRefresh
        // from FERecommenderPanel when staying in with-backup with remaining slots.
        return true
      }

      if (mode === 'deferred') {
        onClearAdditional?.(slotIndex)
        applyLocal()
        showSuccess('Additional FE cleared')
        return true
      }
      if (onClearEngineer) {
        applyLocal()
        void onClearEngineer('additional')
        showSuccess('Additional FE cleared')
        return true
      }
      return true
    } finally {
      setDispatchCardBusy(false)
    }
  }, [mode, onClearAdditional, onClearEngineer, onClearPersist, hasExistingAssignment, showError, showSuccess])

  const handleOpenReassignModal = useCallback(
    (invalid: InvalidAssignmentState) => {
      const replacement = invalid.eligibility.suggestedReplacement
      if (!replacement?.user_id) {
        showError('No eligible replacement engineer is available.')
        return
      }
      setReassignModal({
        stableKey: invalid.stableKey,
        role: invalid.role,
        additionalSlot: invalid.additionalSlot,
        sourceFeUserId: invalid.feUserId,
        sourceFeName: invalid.feName,
        replacementFeUserId: Number(replacement.user_id),
        replacementFeName: replacement.full_name ?? 'Replacement engineer',
        reason: buildReassignmentReasonFromEligibility(invalid.eligibility.reasons),
      })
    },
    [showError],
  )

  /** Phase 8: Open reassignment modal for a voluntary engineer change (clicked different recommendation). */
  const handleVoluntaryReassignModal = useCallback(
    (
      role: 'primary' | 'additional',
      additionalSlot: number | undefined,
      sourceFeUserId: number,
      sourceFeName: string,
      replacementFeUserId: number,
      replacementFeName: string,
      proposedSelection?: FEDispatchSelection | null,
    ) => {
      const stableKey = buildInvalidAssignmentKey(role, sourceFeUserId, additionalSlot)
      setReassignModal({
        stableKey,
        role,
        additionalSlot,
        sourceFeUserId,
        sourceFeName,
        replacementFeUserId,
        replacementFeName,
        reason: 'Voluntary reassignment: different engineer selected',
        proposedSelection: proposedSelection ?? null,
      })
    },
    [],
  )

  const handleSubmitReassignment = useCallback(async () => {
    if (!reassignModal || !ticketId) return
    const sourceSelection =
      reassignModal.role === 'primary'
        ? displaySelection
        : displayBackupSelections[(reassignModal.additionalSlot ?? 1) - 1] ?? null
    if (!sourceSelection) {
      showError('Cannot reassign — assignment not found.')
      return
    }

    if (stageChanges) {
      const proposal = {
        ...(reassignModal.proposedSelection ?? sourceSelection),
        sourceScheduleId: sourceSelection.sourceScheduleId,
        feUserId: reassignModal.replacementFeUserId,
        feName: reassignModal.replacementFeName,
      }
      if (reassignModal.reason.trim()) onPlanningReasonChange?.(reassignModal.reason.trim())
      onConfirm(proposal)
      if (proposal.role === 'primary') setLocalConfirmed(proposal)
      else setLocalConfirmedBackups((prev) => prev.map((a, i) => i === (proposal.additionalSlot ?? 1) - 1 ? proposal : a))
      setInvalidAssignments((prev) => {
        const next = prev.filter((a) => !(a.role === proposal.role && (a.role === 'primary' || a.additionalSlot === proposal.additionalSlot)))
        onInvalidAssignmentChange?.(next.length > 0)
        return next
      })
      setReassignModal(null)
      showSuccess('Replacement proposed', 'Save the parent ticket to apply this change.')
      return
    }

    const scheduleId = resolveScheduleIdForRole(
      reassignModal.role,
      (reassignModal.additionalSlot ?? 1) - 1,
    )
    const { childTicketId, ambiguous } = resolveLinkedChildTicketId(
      sourceSelection,
      scheduleId,
      parentLevelSchedules,
    )

    if (ambiguous) {
      showError('Multiple linked FE visits match this assignment. Resolve manually in the Tasks tab.')
      return
    }
    if (childTicketId == null) {
      showError('No linked FE visit task found for this assignment.')
      return
    }

    // Use proposed selection for schedule context if available (voluntary reassignment),
    // fall back to source selection for eligibility-driven reassignment.
    const selectionForPayload = reassignModal.proposedSelection ?? sourceSelection

    setReassignSubmitting(true)
    try {
      const result = await confirmEngineerReassignment({
        ticketId,
        taskId: childTicketId,
        replacementFeUserId: reassignModal.replacementFeUserId,
        reason: reassignModal.reason.trim() || undefined,
        selection: selectionForPayload,
      })

      const newTaskId = result?.new_task?.id ?? null
      const newScheduleId = result?.new_schedule?.id ?? null

      const replacementSelection: FEDispatchSelection = {
        ...selectionForPayload,
        feUserId: reassignModal.replacementFeUserId,
        feName: reassignModal.replacementFeName,
        role: reassignModal.role,
        additionalSlot: reassignModal.role === 'additional' ? reassignModal.additionalSlot : undefined,
        childTicketId: newTaskId,
        child_ticket_id: newTaskId,
      }

      if (reassignModal.role === 'primary') {
        onConfirm(replacementSelection)
        setLocalConfirmed(replacementSelection)
        setPrimaryDismissed(false)
      } else {
        setLocalConfirmedBackups((prev) => {
          const idx = (reassignModal.additionalSlot ?? 1) - 1
          const next = [...prev]
          next[idx] = replacementSelection
          return next
        })
        onConfirm(replacementSelection)
      }

      setInvalidAssignments((prev) => {
        const resolvedKey = reassignModal.stableKey
        const next = prev.filter((item) => item.stableKey !== resolvedKey)
        onInvalidAssignmentChange?.(next.length > 0)
        return next
      })
      setReassignModal(null)
      showSuccess('Engineer reassigned', 'The FE visit task was superseded and a replacement was created.')
      // Single coordinated refresh — do NOT also call onSchedulesChange
      try {
        await onDispatchReconciled?.()
      } catch {
        showError('Reassignment succeeded but refresh failed. You may need to reload the page.')
      }
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : 'Failed to reassign engineer')
    } finally {
      setReassignSubmitting(false)
    }
  }, [
    reassignModal,
    ticketId,
    displaySelection,
    displayBackupSelections,
    resolveScheduleIdForRole,
    parentLevelSchedules,
    onConfirm,
    onInvalidAssignmentChange,
    onDispatchReconciled,
    showError,
    showSuccess,
  ])

  /** Phase 9: Open withdrawal confirmation modal instead of calling withdrawal directly. */
  const handleOpenWithdrawModal = useCallback(
    (invalid: InvalidAssignmentState) => {
      const scheduleId = resolveScheduleIdForRole(
        invalid.role,
        (invalid.additionalSlot ?? 1) - 1,
      )
      const currentSelection =
        invalid.role === 'primary'
          ? displaySelection
          : displayBackupSelections[(invalid.additionalSlot ?? 1) - 1] ?? null

      const { childTicketId } = resolveLinkedChildTicketId(
        currentSelection ?? { feUserId: invalid.feUserId, feName: invalid.feName } as FEDispatchSelection,
        scheduleId,
        parentLevelSchedules,
      )

      const affectedTasks: { taskId: number; feName: string; role: string }[] = []
      if (childTicketId != null) {
        affectedTasks.push({ taskId: childTicketId, feName: invalid.feName, role: invalid.role })
      }
      if (invalid.role === 'primary') {
        for (const backup of displayBackupSelections) {
          const bScheduleId = resolveScheduleIdForRole('additional', (backup.additionalSlot ?? 1) - 1)
          const { childTicketId: bChildId } = resolveLinkedChildTicketId(backup, bScheduleId, parentLevelSchedules)
          if (bChildId != null) {
            affectedTasks.push({ taskId: bChildId, feName: backup.feName, role: 'additional' })
          }
        }
      }

      setWithdrawModal({
        stableKey: invalid.stableKey,
        role: invalid.role,
        additionalSlot: invalid.additionalSlot,
        feUserId: invalid.feUserId,
        feName: invalid.feName,
        childTicketId,
        affectedTasks,
        reason: 'Dispatch withdrawn from parent-ticket edit',
      })
    },
    [displaySelection, displayBackupSelections, resolveScheduleIdForRole, parentLevelSchedules],
  )

  const handleConfirmWithdrawal = useCallback(async () => {
    if (!withdrawModal) return
    const trimmedReason = withdrawModal.reason.trim()
    if (!trimmedReason) return
    setWithdrawSubmitting(true)
    try {
      if (withdrawModal.role === 'primary') {
        const ok = await handleClearPrimary(trimmedReason)
        if (!ok) return
      } else {
        const ok = await handleClearBackup((withdrawModal.additionalSlot ?? 1) - 1, trimmedReason)
        if (!ok) return
      }
      setInvalidAssignments((prev) => {
        const next = prev.filter((item) => item.stableKey !== withdrawModal.stableKey)
        onInvalidAssignmentChange?.(next.length > 0)
        return next
      })
      setWithdrawModal(null)
      showSuccess(stageChanges ? 'Removal proposed' : 'Dispatch withdrawn', stageChanges ? 'Save the parent ticket to apply the removal. The ticket remains open.' : 'The FE visit was cancelled and the schedule was removed.')
      if (!stageChanges) void onDispatchReconciled?.()
    } catch (err: unknown) {
      showError(err instanceof Error ? err.message : 'Withdrawal failed')
    } finally {
      setWithdrawSubmitting(false)
    }
  }, [withdrawModal, handleClearPrimary, handleClearBackup, onInvalidAssignmentChange, onDispatchReconciled, showError, showSuccess])

  const openAssignFromRecommendation = useCallback(
    async (
      rec: FieldEngineerRecommendation,
      role: 'primary' | 'additional',
      additionalSlot = 1,
    ) => {
      const feUserId = getFeUserId(rec)
      if (stageChanges && !isActivityWindowValid(activityFromLocal, activityToLocal)) {
        showError('Complete the proposed schedule before selecting an engineer. You can still save an incomplete draft.')
        return
      }
      if (feUserId == null || !effectiveReferenceLocation) {
        showError('Cannot assign this engineer. Missing user or location.')
        return
      }

      const assignmentRole = role === 'primary' ? 'primary' : 'additional'
      const primaryFeUserId =
        assignmentRole === 'primary'
          ? null
          : displaySelection?.feUserId ?? primaryEngineer?.user_id ?? null
      const backupFeUserIds =
        assignmentRole === 'primary'
          ? displayBackupSelections.map((backup) => backup.feUserId)
          : displayBackupSelections
              .filter((_, index) => index !== additionalSlot - 1)
              .map((backup) => backup.feUserId)
      const assignmentConflict = getEngineerAssignmentConflict(
        assignmentRole,
        feUserId,
        primaryFeUserId,
        backupFeUserIds,
      )
      if (assignmentConflict) {
        showError(assignmentConflict)
        return
      }

      // Phase 8: If changing engineer on a slot with a linked active task, route through reassignment confirmation.
      const existingSelection =
        role === 'primary'
          ? displaySelection
          : displayBackupSelections[additionalSlot - 1] ?? null
      if (!stageChanges && existingSelection && existingSelection.feUserId !== feUserId) {
        const scheduleId = resolveScheduleIdForRole(role, role === 'primary' ? 0 : additionalSlot - 1)
        const { childTicketId, ambiguous } = resolveLinkedChildTicketId(
          existingSelection,
          scheduleId,
          parentLevelSchedules,
        )
        if (ambiguous) {
          showError('Multiple linked FE visits match this assignment. Resolve manually in the Tasks tab.')
          return
        }
        if (childTicketId != null) {
          const base = preferredScheduleAt ? new Date(preferredScheduleAt) : new Date()
          const dispatchWindow =
            activityFromLocal && activityToLocal && isActivityWindowValid(activityFromLocal, activityToLocal)
              ? activityDateTimesToSelection(activityFromLocal, activityToLocal)
              : buildDefaultDispatchWindow(base)
          const proposed: FEDispatchSelection = buildLiveFEDispatchSelection({
            reference: effectiveReferenceLocation,
            storeId: effectiveStoreId,
            feUserId,
            feName: rec.full_name ?? '',
            dispatchWindow,
            contactNumberOverride: requestContactNumber ?? effectiveReferenceLocation.contactNumber ?? undefined,
            role,
            additionalSlot: role === 'additional' ? additionalSlot : undefined,
            locationSource: effectiveReferenceLocation.coordinateSource ?? null,
          })
          handleVoluntaryReassignModal(
            role,
            role === 'additional' ? additionalSlot : undefined,
            existingSelection.feUserId,
            existingSelection.feName,
            feUserId,
            rec.full_name ?? `FE ${feUserId}`,
            proposed,
          )
          return
        }
      }

      if (role === 'primary' && primaryAssignInFlightRef.current) {
        return
      }
      if (role === 'additional' && backupAssignInFlightRef.current.has(additionalSlot)) {
        return
      }

      if (role === 'primary') setAssigningPrimary(true)
      else setAssigningAdditionalSlotIndex(additionalSlot - 1)

      if (role === 'primary') {
        primaryAssignInFlightRef.current = true
      } else {
        backupAssignInFlightRef.current.add(additionalSlot)
      }

      setDispatchCardBusy(true)
      setDispatchCardBusyMessage(
        role === 'primary' ? 'Saving primary engineer…' : 'Saving additional FE…',
      )

      try {
        const base = preferredScheduleAt ? new Date(preferredScheduleAt) : new Date()
        const dispatchWindow =
          activityFromLocal && activityToLocal && isActivityWindowValid(activityFromLocal, activityToLocal)
            ? activityDateTimesToSelection(activityFromLocal, activityToLocal)
            : buildDefaultDispatchWindow(base)

        const selection: FEDispatchSelection = buildLiveFEDispatchSelection({
          reference: effectiveReferenceLocation,
          storeId: effectiveStoreId,
          feUserId,
          feName: rec.full_name ?? '',
          dispatchWindow,
          contactNumberOverride: requestContactNumber ?? effectiveReferenceLocation.contactNumber ?? undefined,
          role,
          additionalSlot: role === 'additional' ? additionalSlot : undefined,
          locationSource: effectiveReferenceLocation.coordinateSource ?? null,
        })

        const applyLocalAssignment = () => {
          if (stageChanges) {
            selection.sourceScheduleId = existingSelection?.sourceScheduleId
              ?? (role === 'primary' ? existingPrimaryScheduleId : undefined)
            setInvalidAssignments((prev) => {
              const next = prev.filter((a) => !(a.role === role && (role === 'primary' || a.additionalSlot === additionalSlot)))
              onInvalidAssignmentChange?.(next.length > 0)
              return next
            })
          }
          onConfirm(selection)
          if (role === 'primary') {
            setPrimaryDismissed(false)
            setLocalConfirmed(selection)
          } else {
            setLocalConfirmedBackups((prev) => {
              const next = [...prev]
              next[additionalSlot - 1] = selection
              return next
            })
          }
        }

        // Edit mode: persist schedules immediately so backend create/update locks also fire.
        if (onConfirmPersist && !stageChanges) {
          const persistRes = await onConfirmPersist(selection)
          if (!persistRes.success) {
            if (role === 'primary') {
              setAssignFailedSignal((n) => n + 1)
            }
            showError(persistRes.message ?? 'Failed to save engineer assignment')
            return
          }
          applyLocalAssignment()
          showSuccess('Engineer assigned')
          void fetchFEAvailabilityForUsers(recommendDateStr, [feUserId], false)
          void onSchedulesChange?.()
          return
        }

        if (mode === 'deferred') {
          applyLocalAssignment()
          if (!stageChanges) void TicketService.lockFieldEngineer(feUserId)
          showSuccess(stageChanges ? 'Engineer proposed — save to apply' : 'Engineer assigned')
          return
        }

        if (!ticketId) {
          showError('Ticket ID is required to assign engineer.')
          return
        }

        const res = await TicketService.createFESchedule(ticketId, {
          fe_user_id: selection.feUserId,
          schedule_date: selection.scheduleDate,
          schedule_end_date: selection.spansMultipleDays ? (selection.scheduleEndDate ?? null) : null,
          schedule_time: selection.scheduleTime,
          schedule_end_time: selection.scheduleEndTime,
          role,
          latitude: selection.latitude,
          longitude: selection.longitude,
          location_address: selection.storeAddress || null,
          contact_number_override: selection.contactNumberOverride ?? null,
        })
        if (!res.success) {
          showError(res.message ?? 'Failed to assign engineer')
          return
        }

        applyLocalAssignment()
        showSuccess('Engineer assigned')
        void fetchFEAvailabilityForUsers(recommendDateStr, [feUserId], false)
        void onSchedulesChange?.()
      } finally {
        setDispatchCardBusy(false)
        if (role === 'primary') {
          setAssigningPrimary(false)
          primaryAssignInFlightRef.current = false
        } else {
          setAssigningAdditionalSlotIndex(null)
          backupAssignInFlightRef.current.delete(additionalSlot)
        }
      }
    },
    [
      effectiveReferenceLocation,
      effectiveStoreId,
      activityFromLocal,
      activityToLocal,
      fetchFEAvailabilityForUsers,
      mode,
      onConfirm,
      onConfirmPersist,
      onSchedulesChange,
      preferredScheduleAt,
      recommendDateStr,
      requestContactNumber,
      showError,
      showSuccess,
      ticketId,
      displaySelection,
      displayBackupSelections,
      primaryEngineer,
      resolveScheduleIdForRole,
      parentLevelSchedules,
      handleVoluntaryReassignModal,
    ],
  )

  const inputBaseProps = {
    bg: 'white',
    borderColor: colors.inputBorder ?? 'gray.200',
    borderRadius: 'lg',
    size: 'sm' as const,
  }

  const handleChangeStore = useCallback(() => {
    storeHydrationSkippedRef.current = true
    setStoreEditing(true)
    setSitePinConfirmed(false)
  }, [onStoreClear])

  const handleStoreAddressGeocodeToggle = useCallback((checked: boolean) => {
    setUseStoreAddressGeocode(checked)
    setStoreAddressGeocodeError(null)
    setSitePinConfirmed(false)
    if (checked || !localStore) return

    setStoreAddressGeocode(null)
    const nextLocation = storeToReference(localStore)
    if (hasFiniteCoords(nextLocation.latitude, nextLocation.longitude)) {
      setDraftSitePin({
        latitude: Number(nextLocation.latitude),
        longitude: Number(nextLocation.longitude),
      })
      setSitePinOrigin({
        latitude: Number(nextLocation.latitude),
        longitude: Number(nextLocation.longitude),
      })
    }
    const nextScheduleIso = displaySelection
      ? selectionScheduleToIso(displaySelection)
      : preferredScheduleAt ?? null
    void revalidateAssignments({ nextLocation, scheduleIso: nextScheduleIso })
  }, [displaySelection, localStore, preferredScheduleAt, revalidateAssignments])

  const handleSitePinChange = useCallback(
    (latitude: number, longitude: number) => {
      setDraftSitePin({ latitude, longitude })
      setSitePinConfirmed(false)
      setRecommendations([])

      pinReverseAbortRef.current?.abort()
      const controller = new AbortController()
      pinReverseAbortRef.current = controller
      setPinReverseLoading(true)

      void LocationService.reverseGeocode(latitude, longitude, controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return
          const label =
            result?.display_name ?? formatPinnedDisplayName(latitude, longitude)
          if (localStore) {
            setStoreSearchTerm(`[${localStore.storeCode}] ${localStore.storeName}`)
            // Keep store identity; pin override drives distance via draftSitePin.
            return
          }
          const nextLocation: FreeTextLocationResult = {
            type: 'location',
            display_name: label,
            latitude,
            longitude,
            municipality: result?.municipality ?? null,
            province: result?.province ?? null,
            country: result?.country ?? null,
            place_id: result?.place_id,
            source: 'manual_pin',
            address: result?.address,
          }
          setLocalLocation(nextLocation)
          setStoreSearchTerm(label)
          if (!sitePinOrigin) {
            setSitePinOrigin({ latitude, longitude })
          }
        })
        .catch(() => {
          if (controller.signal.aborted) return
          if (!localStore) {
            const label = formatPinnedDisplayName(latitude, longitude)
            setLocalLocation({
              type: 'location',
              display_name: label,
              latitude,
              longitude,
              source: 'manual_pin',
            })
            setStoreSearchTerm(label)
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setPinReverseLoading(false)
        })
    },
    [localStore, sitePinOrigin],
  )

  const handleConfirmSitePin = useCallback(() => {
    if (!effectiveReferenceLocation || !hasCoordinatePair(effectiveReferenceLocation)) return

    const lat = Number(effectiveReferenceLocation.latitude)
    const lng = Number(effectiveReferenceLocation.longitude)
    setDraftSitePin({ latitude: lat, longitude: lng })
    if (!sitePinOrigin) {
      setSitePinOrigin({ latitude: lat, longitude: lng })
    }
    setSitePinConfirmed(true)

    if (effectiveReferenceLocation.type === 'location' && effectiveReferenceLocation.location) {
      onLocationSelected?.({
        ...effectiveReferenceLocation.location,
        latitude: lat,
        longitude: lng,
        source: effectiveReferenceLocation.coordinateSource === 'manual_pin' ? 'manual_pin' : effectiveReferenceLocation.location.source ?? 'nominatim',
      })
    } else if (effectiveReferenceLocation.type === 'store' && effectiveReferenceLocation.store) {
      onStoreSelected?.(effectiveReferenceLocation.store)
    }

    const nextScheduleIso = displaySelection
      ? selectionScheduleToIso(displaySelection)
      : preferredScheduleAt ?? null
    void revalidateAssignments({
      nextLocation: effectiveReferenceLocation,
      scheduleIso: nextScheduleIso,
    })
  }, [
    displaySelection,
    effectiveReferenceLocation,
    onLocationSelected,
    onStoreSelected,
    preferredScheduleAt,
    revalidateAssignments,
    sitePinOrigin,
  ])

  const handleAdjustSitePin = useCallback(() => {
    setSitePinConfirmed(false)
    setRecommendations([])
    setLocalConfirmed(null)
    setLocalConfirmedBackups([])
    setPrimaryDismissed(false)
    onClear?.()
    onClearAdditional?.()
  }, [onClear, onClearAdditional])

  const handleLocationSelect = useCallback((result: CombinedLocationResult) => {
    storeHydrationSkippedRef.current = true
    autoSeededFromAddressRef.current = null
    suppressLocationHydrationRef.current = false
    pendingAddressRefreshRevalidateRef.current = false
    setPrimaryDismissed(false)
    setSeedLocationError(null)
    setSitePinConfirmed(false)
    setRecommendations([])

    if (result.type === 'store') {
      const store = storeResultToClientOption(result)
      const lat = store.latitude ?? store.location?.latitude ?? null
      const lng = store.longitude ?? store.location?.longitude ?? null
      setLocalStore(store)
      setLocalLocation(null)
      setUseStoreAddressGeocode(false)
      setStoreAddressGeocode(null)
      setStoreAddressGeocodeError(null)
      setStoreSearchTerm(`[${store.storeCode}] ${store.storeName}`)
      if (hasFiniteCoords(lat, lng)) {
        setDraftSitePin({ latitude: Number(lat), longitude: Number(lng) })
        setSitePinOrigin({ latitude: Number(lat), longitude: Number(lng) })
      } else {
        setDraftSitePin(null)
        setSitePinOrigin(null)
      }
      onStoreSelected?.(store)
      setStoreEditing(false)
      return
    }

    setLocalStore(null)
    setLocalLocation({ ...result, source: result.source ?? 'nominatim' })
    setUseStoreAddressGeocode(false)
    setStoreAddressGeocode(null)
    setStoreAddressGeocodeError(null)
    setStoreSearchTerm(result.display_name)
    setDraftSitePin({ latitude: result.latitude, longitude: result.longitude })
    setSitePinOrigin({ latitude: result.latitude, longitude: result.longitude })
    onLocationSelected?.(result)
    setStoreEditing(false)
  }, [onStoreSelected, onLocationSelected])

  const renderStoreSearch = (
    <Box>
      <LocationSearchInput
        value={storeSearchTerm}
        onChange={(next) => {
          setStoreSearchTerm(next)
          setSeedLocationError(null)
        }}
        onSelect={handleLocationSelect}
        clientCode={recommendClientCode}
        placeholder="Search store, address, landmark, city..."
        isDisabled={!recommendClientCode || seedLocationLoading}
        inputProps={inputBaseProps}
      />
      {locationValidationErrors && locationValidationErrors.length > 0 ? (
        <VStack align="stretch" spacing={0.5} mt={2}>
          {locationValidationErrors.map((message) => (
            <Text key={message} fontSize="11px" color="red.500">
              {message}
            </Text>
          ))}
        </VStack>
      ) : null}
      {seedLocationLoading ? (
        <HStack mt={2} spacing={2}>
          <Spinner size="xs" />
          <Text fontSize="11px" color={colors.textSecondary}>
            Locating request address and loading FE recommendations...
          </Text>
        </HStack>
      ) : null}
      {seedLocationError ? (
        <Text fontSize="11px" color="orange.600" mt={2}>
          {seedLocationError} You can place a pin on the map below.
        </Text>
      ) : null}
      {!seedLocationLoading && !seedLocationError && localLocation ? (
        <Text fontSize="11px" color="green.700" mt={2}>
          Matched location for FE distance: {localLocation.display_name}
        </Text>
      ) : null}
    </Box>
  )

  const renderStoreGeocodeToggle = localStore && !storeEditing ? (
    <Box
      border="0.5px solid"
      borderColor={useStoreAddressGeocode ? 'blue.200' : colors.borderLight}
      borderRadius="md"
      bg={useStoreAddressGeocode ? 'blue.50' : colors.cardBg}
      px={3}
      py={2}
    >
      <Checkbox
        size="sm"
        colorScheme="blue"
        isChecked={useStoreAddressGeocode}
        isDisabled={storeAddressForGeocode(localStore).length < 3 || storeAddressGeocoding}
        onChange={(event) => handleStoreAddressGeocodeToggle(event.target.checked)}
      >
        <Text fontSize="12px" fontWeight="500">
          Use store address to geocode dispatch location
        </Text>
      </Checkbox>
      <Text fontSize="11px" color={colors.textMuted} mt={1}>
        Keeps the selected store linked, but uses Nominatim coordinates from the store address for map and FE distance.
      </Text>
      {storeAddressGeocoding ? (
        <HStack mt={2} spacing={2}>
          <Spinner size="xs" />
          <Text fontSize="11px" color={colors.textSecondary}>
            Geocoding store address...
          </Text>
        </HStack>
      ) : useStoreAddressGeocode && storeAddressGeocode ? (
        <Text fontSize="11px" color="blue.700" mt={2}>
          Using geocoded coordinates: {storeAddressGeocode.latitude.toFixed(5)}, {storeAddressGeocode.longitude.toFixed(5)}
        </Text>
      ) : storeAddressGeocodeError ? (
        <Text fontSize="11px" color="orange.600" mt={2}>
          {storeAddressGeocodeError}
        </Text>
      ) : null}
    </Box>
  ) : null

  const pinLat = draftSitePin?.latitude ?? effectiveReferenceLocation?.latitude ?? null
  const pinLng = draftSitePin?.longitude ?? effectiveReferenceLocation?.longitude ?? null
  const canConfirmSitePin = hasFiniteCoords(pinLat, pinLng)
  const showApproximatePinHint =
    !sitePinConfirmed &&
    canConfirmSitePin &&
    (Boolean(seedLocationError) || Boolean(localLocation?.isApproximate))
  const pinMovedFromStore =
    Boolean(localStore) &&
    canConfirmSitePin &&
    isPinMovedFromOrigin(
      Number(pinLat),
      Number(pinLng),
      sitePinOrigin?.latitude,
      sitePinOrigin?.longitude,
    )

  const handleSaveStorePin = useCallback(async () => {
    if (!localStore?.storeId || !hasFiniteCoords(pinLat, pinLng)) return
    setSavingStorePin(true)
    try {
      const res = await TicketService.updateStoreDispatchLocation(Number(localStore.storeId), {
        latitude: Number(pinLat),
        longitude: Number(pinLng),
        location_address: effectiveReferenceLocation?.address ?? localStore.branchAddress ?? null,
        normalized_address: effectiveReferenceLocation?.address ?? null,
      })
      if (!res.success) {
        showError(res.message ?? 'Failed to save store pin')
        return
      }
      setSitePinOrigin({ latitude: Number(pinLat), longitude: Number(pinLng) })
      setLocalStore((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          latitude: Number(pinLat),
          longitude: Number(pinLng),
          ...(prev.location
            ? {
                location: {
                  ...prev.location,
                  latitude: Number(pinLat),
                  longitude: Number(pinLng),
                  address: res.data?.address ?? prev.location.address,
                },
              }
            : {}),
        }
      })
      showSuccess('Store location pin saved for future dispatch')
    } finally {
      setSavingStorePin(false)
    }
  }, [
    localStore,
    pinLat,
    pinLng,
    effectiveReferenceLocation?.address,
    showError,
    showSuccess,
  ])

  const renderSitePinControls =
    mode === 'deferred' ? (
      <Box mt={3}>
        {!sitePinConfirmed ? (
          <Box
            border="1px solid"
            borderColor="blue.200"
            borderRadius="md"
            bg="blue.50"
            px={3}
            py={3}
          >
            <Text fontSize="13px" fontWeight="600" color="blue.800" mb={1}>
              Set dispatch site pin
            </Text>
            <Text fontSize="12px" color="blue.700" mb={2}>
              {showApproximatePinHint
                ? 'Matched mall or approximate address — drag the pin to the store entrance, then confirm.'
                : 'Click or drag the pin to the exact site (e.g. mall entrance). Confirm before FE recommendations load.'}
            </Text>
            <DispatchSitePinMap
              latitude={pinLat}
              longitude={pinLng}
              interactive
              isReadOnly={isReadOnly}
              height="320px"
              onPinChange={isReadOnly ? undefined : handleSitePinChange}
            />
            {pinReverseLoading ? (
              <HStack mt={2} spacing={2}>
                <Spinner size="xs" />
                <Text fontSize="12px" color={colors.textSecondary}>
                  Looking up address for pin…
                </Text>
              </HStack>
            ) : null}
            {!isReadOnly ? (
              <Button
                mt={3}
                w="full"
                size="md"
                colorScheme="blue"
                onClick={handleConfirmSitePin}
                isDisabled={!canConfirmSitePin}
              >
                Confirm location and find engineers
              </Button>
            ) : null}
            {!canConfirmSitePin && !isReadOnly ? (
              <Text fontSize="11px" color="orange.700" mt={2}>
                Place a pin on the map to continue.
              </Text>
            ) : null}
          </Box>
        ) : (
          <HStack
            justify="space-between"
            align="center"
            border="1px solid"
            borderColor="green.200"
            borderRadius="md"
            bg="green.50"
            px={3}
            py={2}
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Text fontSize="12px" fontWeight="600" color="green.800">
                Site pin confirmed
              </Text>
              <Text fontSize="11px" color="green.700">
                {effectiveReferenceLocation?.address ||
                  effectiveReferenceLocation?.name ||
                  (canConfirmSitePin
                    ? `${Number(pinLat).toFixed(5)}, ${Number(pinLng).toFixed(5)}`
                    : 'Ready for FE distance')}
              </Text>
            </Box>
            {!isReadOnly ? (
              <HStack spacing={2}>
                {pinMovedFromStore ? (
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="solid"
                    onClick={() => void handleSaveStorePin()}
                    isLoading={savingStorePin}
                  >
                    Save pin to store
                  </Button>
                ) : null}
                <Button size="sm" variant="outline" colorScheme="green" onClick={handleAdjustSitePin}>
                  Adjust pin
                </Button>
              </HStack>
            ) : null}
          </HStack>
        )}
      </Box>
    ) : null

  const hasSelectedDispatchLocation = Boolean(localStore || localLocation || draftSitePin)
  const showLocationSearch =
    mode === 'deferred' &&
    (storeEditing || !hasSelectedDispatchLocation || seedLocationLoading || !sitePinConfirmed)

  const renderLocationControl = (
    <Box>
      {showLocationSearch ? renderStoreSearch : renderStoreGeocodeToggle}
      {renderSitePinControls}
    </Box>
  )

  const { busy: isCardBusy, message: cardBusyMessage } = resolveFeDispatchCardBusy({
    dispatchCardBusy,
    dispatchCardBusyMessage,
    recommendLoading,
    assigningPrimary,
    assigningAdditionalSlotIndex,
    recommendScheduleLoading,
  })

  return (
    <VStack align="stretch" spacing={0}>
      <Box position="relative">
      <Box
        border="0.5px solid"
        borderColor={colors.borderLight}
        borderRadius="md"
        bg={colors.cardBg}
        px={3}
        py={3}
        mb={3}
        opacity={isCardBusy ? 0.8 : 1}
        pointerEvents={isCardBusy ? 'none' : 'auto'}
      >
        <Checkbox
          size="sm"
          colorScheme="blue"
          isChecked={certificationRequired}
          isDisabled={isReadOnly || isCardBusy}
          onChange={(e) => {
            const checked = e.target.checked
            emitCertificationChange(checked, checked ? brandId : null)
          }}
        >
          <Text as="span" fontSize="sm" fontWeight="500">
            Certification Required
          </Text>
        </Checkbox>
        {certificationRequired ? (
          <FormControl mt={3} isRequired>
            <MockupFieldLabel>Brand *</MockupFieldLabel>
            <Select
              placeholder={brandsLoading ? 'Loading...' : 'Select Brand'}
              value={brandId != null ? String(brandId) : ''}
              isDisabled={isReadOnly || brandsLoading || brands.length === 0 || isCardBusy}
              onChange={(e) => {
                const nextId = e.target.value ? Number(e.target.value) : null
                emitCertificationChange(true, nextId != null && Number.isFinite(nextId) ? nextId : null)
              }}
              {...inputBaseProps}
            >
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </Select>
            {!brandsLoading && brands.length === 0 ? (
              <FormHelperText fontSize="xs" color="orange.600">
                No brands configured for this Client/Project.
              </FormHelperText>
            ) : null}
          </FormControl>
        ) : null}
      </Box>

      {mode !== 'deferred' && effectiveStoreDetails?.storeName ? (
        <Box mb={2} opacity={isCardBusy ? 0.8 : 1} pointerEvents={isCardBusy ? 'none' : 'auto'}>
          <SectionLabel>Store / site location</SectionLabel>
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" fontWeight="600" color={colors.text}>
              [{effectiveStoreDetails.branchCode}] {effectiveStoreDetails.storeName}
            </Text>
            {effectiveStoreDetails.storeAddress && (
              <Text fontSize="xs" color={colors.textSecondary}>{effectiveStoreDetails.storeAddress}</Text>
            )}
            <ScheduleMapPreview
              latitude={effectiveStoreDetails.latitude != null ? String(effectiveStoreDetails.latitude) : undefined}
              longitude={effectiveStoreDetails.longitude != null ? String(effectiveStoreDetails.longitude) : undefined}
            />
          </VStack>
        </Box>
      ) : null}

      {/* FE dispatch */}
      <Box opacity={isCardBusy ? 0.8 : 1} pointerEvents={isCardBusy ? 'none' : 'auto'}>
        {!canRecommendFE && recommendFEDisabledReason && !(mode === 'deferred' && showLocationSearch) && (
          <Text fontSize="xs" color={colors.textMuted} mt={2}>{recommendFEDisabledReason}</Text>
        )}
        {canRecommendFE || (mode === 'deferred' && showLocationSearch) ? (
          <>
          {invalidAssignments.length > 0 ? (
            <VStack align="stretch" spacing={2} mb={3}>
              {invalidAssignments.map((invalid) => (
                <Alert key={invalid.stableKey} status="warning" borderRadius="md">
                  <AlertIcon />
                  <Box flex="1">
                    <AlertDescription fontWeight="600" mb={1}>
                      {invalid.feName} ({invalid.role === 'primary' ? 'Primary' : `Additional #${invalid.additionalSlot ?? ''}`}) — No longer eligible
                    </AlertDescription>
                    {invalid.eligibility.reasons.length > 0 ? (
                      <UnorderedList fontSize="xs" pl={4} spacing={0.5}>
                        {invalid.eligibility.reasons.map((reason) => (
                          <ListItem key={reason}>{reason}</ListItem>
                        ))}
                      </UnorderedList>
                    ) : null}
                    {invalid.eligibility.suggestedReplacement ? (
                      <Text fontSize="xs" mt={2} color={colors.textSecondary}>
                        Suggested replacement: {invalid.eligibility.suggestedReplacement.full_name}
                      </Text>
                    ) : null}
                    <HStack mt={3} spacing={2} flexWrap="wrap">
                      {invalid.eligibility.suggestedReplacement ? (
                        <Button
                          size="xs"
                          colorScheme="blue"
                          onClick={() => handleOpenReassignModal(invalid)}
                          isDisabled={isReadOnly || isCardBusy}
                        >
                          Reassign engineer
                        </Button>
                      ) : null}
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => handleOpenWithdrawModal(invalid)}
                        isDisabled={isReadOnly || isCardBusy}
                      >
                        {stageChanges ? 'Remove proposed assignment' : invalid.role === 'primary' ? 'Withdraw dispatch' : 'Remove additional'}
                      </Button>
                    </HStack>
                  </Box>
                </Alert>
              ))}
            </VStack>
          ) : null}
          <FERecommenderPanel
            key={`${effectiveReferenceLocation?.type ?? 'none'}:${effectiveStoreId ?? effectiveReferenceLocation?.name ?? 'none'}:${sitePinConfirmed ? 'c' : 'd'}:${pinLat ?? ''},${pinLng ?? ''}`}
            recommendations={recommendations}
            loading={recommendLoading}
            assigningPrimary={assigningPrimary}
            assigningAdditionalSlotIndex={assigningAdditionalSlotIndex}
            isReadOnly={isReadOnly}
            disableAutoAssignWhenAssigned={Boolean(hasExistingAssignment)}
            primaryEngineerId={primaryEngineerUserId}
            primaryEngineerName={primaryEngineerName}
            additionalSlots={additionalSlots}
            referenceType={effectiveReferenceLocation?.type}
            referenceName={effectiveReferenceLocation?.name}
            referenceCode={effectiveReferenceLocation?.code}
            storeName={effectiveStoreDetails?.storeName}
            storeCode={effectiveStoreDetails?.branchCode ?? localStore?.storeCode}
            storeLatitude={effectiveStoreDetails?.latitude}
            storeLongitude={effectiveStoreDetails?.longitude}
            ticketId={ticketId}
            preferredScheduleAt={activeScheduleIso}
            certificationLabel={effectiveCertificationLabel}
            requiredCertifications={effectiveRequiredCertifications}
            activityScheduleFrom={activityFromLocal}
            activityScheduleTo={activityToLocal}
            supportDurationOptions={supportDurationOptions}
            initialManualEndOverride={Boolean(initialPlanningWindow?.manualOverride)}
            initialManualEndOverrideReason={initialPlanningWindow?.overrideReason}
            onActivityScheduleChange={handleActivityScheduleChange}
            onActivityScheduleApply={handleActivityScheduleApply}
            feAvailabilityByUser={feAvailabilityByUser}
            scheduleDateStr={recommendDateStr}
            onEditStore={mode === 'deferred' && !isReadOnly ? handleChangeStore : onStoreClear}
            storeSearchSlot={mode === 'deferred' ? renderLocationControl : undefined}
            showOverviewMap={mode !== 'deferred' || sitePinConfirmed}
            onSelectPrimary={(rec) => void openAssignFromRecommendation(rec, 'primary')}
            onSelectAdditional={(rec, slotIndex) => void openAssignFromRecommendation(rec, 'additional', slotIndex + 1)}
            onClearPrimary={handleClearPrimary}
            onClearAdditional={(slotIndex) => handleClearBackup(slotIndex)}
            onAddAdditionalSlot={() => setWantsAdditional(true)}
            onScheduleChange={handleScheduleConflictChange}
            onPromoteAdditional={handlePromoteAdditional}
            onFindNewPrimary={handleFindNewPrimary}
            onRefresh={() => void fetchRecommendations({ limit: 50 })}
            onManualModeOpen={() => void fetchRecommendations({ limit: 50 })}
            wantsAdditional={wantsAdditional}
            onWantsAdditionalChange={(checked) => {
              void handleWantsBackupChange(checked)
            }}
            assignFailedSignal={assignFailedSignal}
          />
          </>
        ) : null}
        {mode === 'immediate' && feScheduleRefreshing ? (
          <HStack py={4} gap={3} mt={2}>
            <Spinner size="sm" color={colors.primary} />
            <Text fontSize="sm" color={colors.textSecondary}>Loading FE schedule...</Text>
          </HStack>
        ) : null}
      </Box>

      {isCardBusy ? (
        <Flex
          position="absolute"
          inset={0}
          zIndex={20}
          align="center"
          justify="center"
          bg="blackAlpha.100"
          borderRadius="md"
        >
          <HStack
            spacing={2}
            bg={colors.cardBg}
            px={3}
            py={2}
            borderRadius="md"
            border="1px solid"
            borderColor={colors.borderLight}
            boxShadow="sm"
          >
            <Spinner size="sm" color={colors.brandBlue ?? 'blue.500'} thickness="2px" />
            <Text fontSize="12px" color={colors.textMuted}>
              {cardBusyMessage}
            </Text>
          </HStack>
        </Flex>
      ) : null}
      </Box>

      {/* Timeline modal */}
      <Modal isOpen={isRecommendOpen} onClose={onRecommendClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="90vw">
          <ModalHeader pr={12}>
            <HStack justify="space-between" flexWrap="wrap" gap={2}>
              <Text>Recommended field engineers</Text>
              <HStack flexShrink={0} spacing={2}>
                <IconButton
                  aria-label="Previous day"
                  size="sm"
                  variant="outline"
                  icon={<ChevronLeft size={16} />}
                  onClick={() =>
                    setRecommendSelectedDate((d) => {
                      const next = new Date(d)
                      next.setDate(next.getDate() - 1)
                      return next
                    })
                  }
                />
                <Input
                  type="date"
                  size="sm"
                  value={recommendDateStr}
                  onChange={(e) => {
                    const v = e.target.value
                    if (v) setRecommendSelectedDate(new Date(v))
                  }}
                  maxW="160px"
                />
                <IconButton
                  aria-label="Next day"
                  size="sm"
                  variant="outline"
                  icon={<ChevronRight size={16} />}
                  onClick={() =>
                    setRecommendSelectedDate((d) => {
                      const next = new Date(d)
                      next.setDate(next.getDate() + 1)
                      return next
                    })
                  }
                />
                <Button
                  size="sm"
                  variant={isSelectedToday ? 'solid' : 'outline'}
                  colorScheme="blue"
                  onClick={() => setRecommendSelectedDate(new Date())}
                >
                  Today
                </Button>
              </HStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            {hasFeAssignment && (
              <Text fontSize="xs" color={colors.textMuted} mb={3}>
                To change the field engineer, pick an <strong>Available</strong> slot on another engineer&apos;s row for the desired date.
              </Text>
            )}
            {recommendLoading ? (
              <HStack justify="center" py={8}><Spinner size="lg" /></HStack>
            ) : recommendations.length === 0 ? (
              <Text color={colors.textSecondary}>No recommendations. Ensure the store has location data.</Text>
            ) : recommendScheduleLoading ? (
              <HStack py={12} justify="center" gap={3}>
                <Spinner size="md" color={colors.primary} />
                <Text fontSize="sm" color={colors.textSecondary}>Loading FE schedule...</Text>
              </HStack>
            ) : (
              <TableContainer overflowX="auto" whiteSpace="nowrap">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th minW="220px" borderRight="1px solid" borderColor={colors.borderLight} bg={colors.bg}>
                        FE Name / Info
                      </Th>
                      {timelineHours.map((hour) => (
                        <Th key={hour} textAlign="center" px={1} fontSize="xs" minW="60px" border="1px solid" borderColor={colors.borderLight}>
                          {String(hour).padStart(2, '0')}:00
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {recommendations.map((rec, idx) => {
                      const feUserId = getFeUserId(rec)
                      const timelineBlocks = feUserId != null ? getTimelineBlocks(feUserId) : []
                      return (
                        <Tr key={rec.field_engineer_id ?? idx}>
                          <Td borderRight="1px solid" borderColor={colors.borderLight} py={2} minW="220px" bg={colors.cardBg}>
                            <VStack align="stretch" spacing={0.5}>
                              <Text fontWeight="600" fontSize="sm">{rec.full_name}</Text>
                              <Text fontSize="xs" color={colors.textSecondary}>
                                Score: {rec.score ?? '—'}
                                {rec.distance_km != null ? ` · ${rec.distance_km} km` : ''}
                              </Text>
                              <Text fontSize="xs" color={colors.textSecondary}>
                                Workload: {rec.workload ?? '—'} · Cluster: {rec.cluster ?? '—'}
                              </Text>
                              {rec.has_required_certifications && (
                                <Badge size="sm" colorScheme="green" fontSize="xs" w="fit-content">✓ Certs</Badge>
                              )}
                            </VStack>
                          </Td>
                          {timelineBlocks.length > 0 ? timelineBlocks.map((block, blockIdx) => {
                            const span = block.endHour - block.startHour
                            const timeStr = `${String(block.startHour).padStart(2, '0')}:00`
                            const isAvailable = block.schedule === null
                            const canEditSchedule = block.schedule?.is_current_ticket === true
                            return (
                              <Td
                                key={`${block.startHour}-${block.endHour}-${blockIdx}`}
                                colSpan={span}
                                px={1}
                                py={1}
                                cursor={isAvailable || canEditSchedule ? 'pointer' : 'default'}
                                onClick={() => {
                                  if (isAvailable) {
                                    void openAssignFromRecommendation(rec, 'primary')
                                  } else if (canEditSchedule && block.schedule) {
                                    showError('Schedule editing is handled in the dispatch schedule panel.')
                                  }
                                }}
                                _hover={isAvailable || canEditSchedule ? { bg: colors.borderLight } : undefined}
                                border="1px solid"
                                borderColor={colors.borderLight}
                                bg={block.schedule ? 'orange.50' : undefined}
                              >
                                {isAvailable ? (
                                  <Text fontSize="xs" color={colors.textSecondary}>Available</Text>
                                ) : block.schedule ? (
                                  <Box fontSize="xs">
                                    <Text fontWeight="600">
                                      {block.schedule.schedule_time ?? '—'}
                                      {block.schedule.schedule_end_time ? `–${block.schedule.schedule_end_time}` : ''}
                                    </Text>
                                    <Text noOfLines={2}>{block.schedule.ticket_subject || '—'}</Text>
                                  </Box>
                                ) : null}
                              </Td>
                            )
                          }) : (
                            <Td colSpan={24} px={2} py={2} fontSize="xs" color={colors.textMuted}>
                              Missing user account — cannot schedule
                            </Td>
                          )}
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={reassignModal != null}
        onClose={() => !reassignSubmitting && setReassignModal(null)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reassign field engineer</ModalHeader>
          <ModalCloseButton isDisabled={reassignSubmitting} />
          <ModalBody>
            <Text fontSize="sm" mb={1}>
              <strong>{reassignModal?.sourceFeName}</strong> ({reassignModal?.role === 'primary' ? 'Primary' : `Additional #${reassignModal?.additionalSlot ?? ''}`})
            </Text>
            <Text fontSize="sm" mb={3}>
              Replace with <strong>{reassignModal?.replacementFeName}</strong> using the current schedule and location context.
            </Text>
            <FormControl>
              <MockupFieldLabel>Reason</MockupFieldLabel>
              <Textarea
                size="sm"
                value={reassignModal?.reason ?? ''}
                onChange={(e) =>
                  setReassignModal((prev) => (prev ? { ...prev, reason: e.target.value } : prev))
                }
                rows={3}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setReassignModal(null)} isDisabled={reassignSubmitting}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={() => void handleSubmitReassignment()} isLoading={reassignSubmitting}>
              Confirm reassignment
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Phase 9: Withdrawal confirmation modal */}
      <Modal
        isOpen={withdrawModal != null}
        onClose={() => !withdrawSubmitting && setWithdrawModal(null)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{stageChanges ? 'Propose dispatch removal' : 'Confirm dispatch withdrawal'}</ModalHeader>
          <ModalCloseButton isDisabled={withdrawSubmitting} />
          <ModalBody>
            <Text fontSize="sm" mb={2}>
              <strong>{withdrawModal?.feName}</strong> ({withdrawModal?.role === 'primary' ? 'Primary' : `Additional #${withdrawModal?.additionalSlot ?? ''}`})
            </Text>
            {withdrawModal?.childTicketId != null ? (
              <Text fontSize="sm" mb={2} color={colors.textSecondary}>
                Task #{withdrawModal.childTicketId} will be cancelled and retained as historical data. The active schedule will be removed.
              </Text>
            ) : (
              <Text fontSize="sm" mb={2} color={colors.textSecondary}>
                The FE assignment and schedule will be removed.
              </Text>
            )}
            {withdrawModal?.role === 'primary' && displayBackupSelections.length > 0 ? (
              <Alert status="warning" borderRadius="md" mb={3}>
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  Withdrawing the primary engineer will also withdraw all additional engineers.
                </AlertDescription>
              </Alert>
            ) : null}
            {withdrawModal?.affectedTasks && withdrawModal.affectedTasks.length > 1 ? (
              <Box mb={3}>
                <Text fontSize="xs" fontWeight="600" mb={1}>Affected tasks:</Text>
                <UnorderedList fontSize="xs" pl={4} spacing={0.5}>
                  {withdrawModal.affectedTasks.map((t) => (
                    <ListItem key={t.taskId}>
                      Task #{t.taskId} — {t.feName} ({t.role})
                    </ListItem>
                  ))}
                </UnorderedList>
              </Box>
            ) : null}
            <FormControl>
              <MockupFieldLabel>Reason</MockupFieldLabel>
              <Textarea
                size="sm"
                value={withdrawModal?.reason ?? ''}
                onChange={(e) =>
                  setWithdrawModal((prev) => (prev ? { ...prev, reason: e.target.value } : prev))
                }
                rows={3}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setWithdrawModal(null)} isDisabled={withdrawSubmitting}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={() => void handleConfirmWithdrawal()}
              isLoading={withdrawSubmitting}
              isDisabled={!withdrawModal?.reason.trim()}
            >
              {stageChanges ? 'Stage removal' : 'Confirm withdrawal'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </VStack>
  )
}
